import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { io as socketIOClient } from 'socket.io-client';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import * as XLSX from 'xlsx';
import { baseColumnDefs as baseDatosColumnDefs, parseFechaPedido, parseFechaDDMMYYYY } from './baseDatosColumns';
import { API_BASE_URL, SOCKET_URL } from './config';
import { getUserRoles } from './userRoles';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

ModuleRegistry.registerModules([AllCommunityModule]);

const COLUMN_VISIBILITY_STORAGE_KEY = 'baseDatosColumnVisibility';
const COLUMN_WIDTHS_STORAGE_KEY = 'baseDatosColumnWidths';
const NUEVO_ESTATUS_STICKY_STORAGE_KEY = 'baseDatosNuevoEstatusStickyByPedidoItem';
const LIVE_UPDATE_DELAY_MS = 500;
const SELF_UPDATE_SUPPRESSION_MS = 1800;
const QUICK_VIEW_TRIPLE_CLICK_WINDOW_MS = 550;
const EXCEL_EPOCH_OFFSET = 25569;
const MS_PER_DAY = 86400 * 1000;
const ADJUNTOS_MAX_PER_REGISTRO = 10;
const ADJUNTOS_ACCEPTED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx,.txt';
const ADJUNTOS_IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp'
]);

const excelSerialToDate = (serial) => {
  if (typeof serial !== 'number' || Number.isNaN(serial)) return null;
  const milliseconds = Math.round((serial - EXCEL_EPOCH_OFFSET) * MS_PER_DAY);
  const date = new Date(milliseconds);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds()
  );
};

const parseCalendarInputDate = (value, toEndOfDay = false) => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(part => Number(part));
  if ([year, month, day].some(num => Number.isNaN(num))) return null;
  const date = new Date(year, month - 1, day, toEndOfDay ? 23 : 0, toEndOfDay ? 59 : 0, toEndOfDay ? 59 : 0, toEndOfDay ? 999 : 0);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseDateValue = (value) => {
  if (value === null || typeof value === 'undefined' || value === '') return null;
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return excelSerialToDate(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      const numericValue = Number(trimmed);
      if (!Number.isNaN(numericValue)) {
        const serialDate = excelSerialToDate(numericValue);
        if (serialDate) return serialDate;
      }
    }
    const parsedDDMM = parseFechaDDMMYYYY(trimmed);
    if (parsedDDMM && !Number.isNaN(parsedDDMM.getTime())) {
      return parsedDDMM;
    }
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  } else {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  return null;
};

const formatFileSizeLabel = (bytes) => {
  const numeric = Number(bytes);
  if (!Number.isFinite(numeric) || numeric < 0) return '';
  if (numeric >= 1024 * 1024) {
    return `${(numeric / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (numeric >= 1024) {
    return `${(numeric / 1024).toFixed(1)} KB`;
  }
  return `${numeric} B`;
};

const formatAdjuntoFecha = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

const isImageMimeType = (mimeType) => {
  if (!mimeType || typeof mimeType !== 'string') return false;
  return ADJUNTOS_IMAGE_MIME_TYPES.has(mimeType.toLowerCase());
};

const getAdjuntoExtension = (adjunto) => {
  const rawName = adjunto?.file_name || adjunto?.original_name || '';
  const normalized = String(rawName).trim();
  const index = normalized.lastIndexOf('.');
  if (index < 0 || index === normalized.length - 1) return '';
  return normalized.slice(index + 1).toUpperCase();
};

const isPdfAdjunto = (adjunto) => {
  const mimeType = (adjunto?.mime_type || '').toString().toLowerCase();
  if (mimeType === 'application/pdf') return true;
  return getAdjuntoExtension(adjunto) === 'PDF';
};

const clampPreviewZoom = (value) => Math.min(8, Math.max(1, value));

const normalizeHeaderKey = (key) => {
  if (key === null || key === undefined) return '';
  return key
    .toString()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9_\s]/g, '')
    .replace(/\s+/g, '_');
};

const NUEVO_ESTATUS_EXCEL_HEADER_ALIASES = new Map([
  ['ASEGURADORA_SUCURSAL', 'ASEGURADORA'],
  ['ASEGURADORA__SUCURSAL', 'ASEGURADORA'],
  ['ANO', 'ANIO'],
  ['ANO_MODELO', 'ANIO'],
  ['COLUMNA_1', 'COLUMNA1'],
  ['DIAS_ENTREGADO_1', 'DIAS_ENTREGADO1'],
  ['DIAS_ENTREGADO_2', 'DIAS_ENTREGADO2'],
  ['DIAS_ENTREGADO_1_FECHA_PEDIDO_VS_FECHA_ENTREGADO', 'DIAS_ENTREGADO1'],
  ['DIAS_ENTREGADO_1_FECHA_PROMESSA_DE_ENTREGA_VS_FECHA_ENTREGADO', 'DIAS_ENTREGADO2'],
  ['DIAS_ENTREGADO_1_FECHA_PROMESA_DE_ENTREGA_VS_FECHA_ENTREGADO', 'DIAS_ENTREGADO2'],
  ['DIAS_DIAS_RECIBIDO1', 'DIAS_RECIBIDO1'],
  ['DIAS_DIAS_RECIBIDO2', 'DIAS_RECIBIDO2'],
  ['DIAS_RECIBIDO_1', 'DIAS_RECIBIDO1'],
  ['DIAS_RECIBIDO_2', 'DIAS_RECIBIDO2'],
  ['DIAS_RECIBIDO_1_FECHA_PEDIDO_VS_FECHA_RECIBIDO', 'DIAS_RECIBIDO1'],
  ['DIAS_RECIBIDO_1_FECHA_ENTREGADO_VS_FECHA_RECIBIDO', 'DIAS_RECIBIDO2'],
  ['FECHA_DE_CANCELACION_DE_PIEZA', 'FECHA_CANCELACION_DE_PIEZA'],
  ['FECHA_CANCELACION_PIEZA', 'FECHA_CANCELACION_DE_PIEZA']
]);

const normalizeKeyPart = (value) => (value ?? '').toString().trim().toUpperCase();

const pickRowValue = (rowData, candidateKeys = []) => {
  if (!rowData || typeof rowData !== 'object') return '';
  for (const key of candidateKeys) {
    const value = rowData[key];
    if (value !== null && typeof value !== 'undefined' && String(value).trim() !== '') {
      return value;
    }
  }
  return '';
};

const buildPedidoItemKey = (pedido, item) => {
  const pedidoKey = normalizeKeyPart(pedido);
  const itemKey = normalizeKeyPart(item);
  if (!pedidoKey && !itemKey) return '';
  return `${pedidoKey}|||${itemKey}`;
};


const isQualitasNuevoEstatusRow = (row) =>
  normalizeKeyPart(row?.ASEGURADORA).includes('QUALITAS');

const normalizeQualitasPrecio = (value) => {
  const raw = String(value ?? '').trim().replace(/[^0-9,.-]/g, '');
  if (!raw) return '';
  const normalized = raw.includes(',')
    ? raw.replace(/\./g, '').replace(',', '.')
    : raw.replace(/,/g, '');
  const n = Number(normalized);
  return Number.isFinite(n) ? String(n) : normalizeKeyPart(value);
};

const buildQualitasNuevoEstatusProjectionKey = (row) => [
  'QUALITAS',
  normalizeKeyPart(row?.SINIESTRO),
  normalizeKeyPart(row?.ITEM),
  normalizeKeyPart(row?.BACK_ORDER),
  normalizeQualitasPrecio(row?.PRECIO),
  normalizeKeyPart(row?.OC)
].join('|||');

const resolveQualitasNuevoEstatusRow = (baseRow, nuevoRows, baseRows) => {
  const siniestro = normalizeKeyPart(baseRow?.SINIESTRO);
  const item = normalizeKeyPart(baseRow?.ITEM);
  const backOrder = normalizeKeyPart(baseRow?.BACK_ORDER);

  let candidates = nuevoRows.filter(row =>
    isQualitasNuevoEstatusRow(row) &&
    normalizeKeyPart(row?.SINIESTRO) === siniestro &&
    normalizeKeyPart(row?.ITEM) === item &&
    normalizeKeyPart(row?.BACK_ORDER) === backOrder
  );

  let peers = baseRows.filter(row =>
    isQualitasNuevoEstatusRow(row) &&
    normalizeKeyPart(row?.SINIESTRO) === siniestro &&
    normalizeKeyPart(row?.ITEM) === item &&
    normalizeKeyPart(row?.BACK_ORDER) === backOrder
  );

  if (!candidates.length) return null;
  if (candidates.length === 1 && peers.length <= 1) return candidates[0];

  const precio = normalizeQualitasPrecio(baseRow?.PRECIO);
  if (precio) {
    candidates = candidates.filter(row =>
      normalizeQualitasPrecio(row?.PRECIO) === precio
    );
    peers = peers.filter(row =>
      normalizeQualitasPrecio(row?.PRECIO) === precio
    );
  }

  if (candidates.length === 1 && peers.length <= 1) return candidates[0];

  const oc = normalizeKeyPart(baseRow?.OC);
  candidates = candidates.filter(row =>
    normalizeKeyPart(row?.OC) === oc
  );
  peers = peers.filter(row =>
    normalizeKeyPart(row?.OC) === oc
  );

  return candidates.length === 1 && peers.length <= 1
    ? candidates[0]
    : null;
};

const buildSiniestroItemKey = (siniestro, item) => {
  const siniestroKey = normalizeKeyPart(siniestro);
  const itemKey = normalizeKeyPart(item);
  if (!siniestroKey && !itemKey) return '';
  return `${siniestroKey}|||${itemKey}`;
};

const ALLOWED_LOCALIDADES = ['local', 'foraneo'];
const COMPAQ_OPTIONS = ['GENERADO', 'GENERAR'];
const ESTATUS_CANCELACION_OPTIONS = ['RECUPERAR PIEZA', 'RECUPERAR EFECTIVO', 'RECUPERADO', 'NO SURTIDO', 'DEVOLUCION A PROVEEDOR', 'REASIGNADO', ' '];
const ESTATUS2_OPTIONS = ['', 'ACUSE PENDIENTE', 'ACUSE CORREO', 'CORREO', 'CAMBIO', 'CAMBIO OK', 'FACTURADO', 'FACTURAR', 'PAGADO', 'RECUPERAR EFECTIVO', 'PENDIENTE', 'TERMINADO', 'MENSAJE'];
const normalizeAseguradoraValue = (value) => (
  (value ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
);
const isMultiasistenciaAseguradora = (value) => normalizeAseguradoraValue(value).includes('multiasistencia');
const isQualitasXnuupAseguradora = (value) => { const aseguradora = normalizeAseguradoraValue(value); return aseguradora.includes('qualitas') || aseguradora.includes('xnuup') || aseguradora.includes('xnuub'); };
const isQualitasBackOrderRCanceladoRow = (row) => isQualitasXnuupAseguradora(row?.ASEGURADORA) && String(row?.BACK_ORDER ?? '').trim().toLowerCase().endsWith('-r') && String(row?.NUEVO_ESTATUS ?? '').trim().toLowerCase() === 'cancelado';
const isValeManualRow = (row) => normalizeAseguradoraValue(row?.ESTATUS).includes('vale manual');
const canEditNuevoEstatusForRow = (row) => isMultiasistenciaAseguradora(row?.ASEGURADORA) || isValeManualRow(row);
const ESTATUS_EDITABLE_FIELDS = new Set(['NUEVO_ESTATUS', 'ESTATUS2', 'ESTATUS3', 'ESTATUS_DE_PAGO', 'DIAS_ENTREGADO1', 'BACK_ORDER']);
const ESTATUS_DE_PAGO_OPTIONS = ['', 'PAGADO', 'FACTURADO', 'FACTURAR', 'CANCELADO', 'REFACTURAR'];
const normalizeFacturacionSyncStatus = (value) => (
  String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
);
const FACTURACION_SYNC_STATUS_MAP = new Map([
  ['facturar', 'FACTURAR'],
  ['facturado', 'FACTURADO'],
  ['pagado', 'PAGADO']
]);
const getFacturacionSyncStatusUpdate = (value) => {
  const normalized = normalizeFacturacionSyncStatus(value);
  if (normalized === '') {
    return { shouldSync: true, value: '' };
  }
  const mapped = FACTURACION_SYNC_STATUS_MAP.get(normalized);
  if (!mapped) {
    return { shouldSync: false, value: '' };
  }
  return { shouldSync: true, value: mapped };
};
const CAPTURA_EDITABLE_FIELDS = new Set(['CODIGO', 'CHOFER', 'COSTO', 'COMPAQ', 'UUID', 'NUMERO DE FACTURA', 'FECHA_CANCELACION_DE_PIEZA', 'OC']);
const ESTATUS_CANCELACION_PAUSA = new Set(['RECUPERADO', 'NO SURTIDO']);
const NUEVO_ESTATUS_DATE_FIELD_OPTIONS = [
  { value: 'FECHA_CANCELACION_DE_PIEZA', label: 'Fecha cancelación de pieza' },
  { value: 'FECHA_PEDIDO', label: 'Fecha pedido' },
  { value: 'PROMESA_DE_ENTREGA', label: 'Promesa de entrega' }
];

const getSelectFieldOptions = (field) => {
  if (field === 'LOCALIDAD') return ALLOWED_LOCALIDADES;
  if (field === 'COMPAQ') return COMPAQ_OPTIONS;
  if (field === 'ESTATUS2') return ESTATUS2_OPTIONS;
  if (field === 'ESTATUS_DE_PAGO') return ESTATUS_DE_PAGO_OPTIONS;
  if (field === 'DIAS_ENTREGADO1') return ESTATUS_CANCELACION_OPTIONS;
  return null;
};

const normalizeCancelacionStatus = (value) => {
  if (value == null) return '';
  return String(value).trim().toUpperCase();
};

const isRecoveringCancelacionStatus = (normalizedStatus) => normalizedStatus.includes('RECUPERAR');

const buildDiasSinRecuperarLabel = (backOrderValue, fechaCancelacionValue, nowDate = new Date()) => {
  const backOrderDate = parseDateValue(backOrderValue);
  const fechaCancelacionDate = parseDateValue(fechaCancelacionValue);
  const referenceDate = (backOrderDate && !Number.isNaN(backOrderDate.getTime()))
    ? backOrderDate
    : ((fechaCancelacionDate && !Number.isNaN(fechaCancelacionDate.getTime())) ? fechaCancelacionDate : null);
  if (!referenceDate) return '';
  const startReferenceDate = new Date(referenceDate);
  startReferenceDate.setHours(0, 0, 0, 0);
  const startNow = new Date(nowDate);
  startNow.setHours(0, 0, 0, 0);
  const diffMs = startNow.getTime() - startReferenceDate.getTime();
  if (Number.isNaN(diffMs)) return '';
  const diffDays = Math.max(0, Math.floor(diffMs / MS_PER_DAY));
  return `${diffDays} dias`;
};

const BaseDatosPage = () => {
  const gridRef = useRef(null);
  const nuevoEstatusGridRef = useRef(null);
  const adjuntosFileInputRef = useRef(null);
  const nuevoEstatusFileInputRef = useRef(null);
  const dialogInputRef = useRef(null);
  const liveUpdateTimersRef = useRef(new Map());
  const selfUpdateTimestampRef = useRef(0);
  const undoStackRef = useRef([]);
  const pendingUndoRef = useRef(null);
  const suppressHistoryRef = useRef(false);
  const revertingRef = useRef(false);
  const inspectorCommitRef = useRef(false);
  const inspectorSuppressCommitRef = useRef(false);
  const inspectorFocusTrackerRef = useRef({ field: null, rowKey: null });
  const nuevoEstatusStickyValuesRef = useRef(new Map());
  const suppressedCellValueChangesRef = useRef(new Set());
  const principalQuickViewClickRef = useRef({ key: '', count: 0, lastAt: 0 });
  const nuevoEstatusQuickViewClickRef = useRef({ key: '', count: 0, lastAt: 0 });

  const persistNuevoEstatusStickyMap = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      const payload = Object.fromEntries(nuevoEstatusStickyValuesRef.current.entries());
      localStorage.setItem(NUEVO_ESTATUS_STICKY_STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      console.warn('No se pudo persistir el historial de Nuevo estatus:', err);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem(NUEVO_ESTATUS_STICKY_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;
      const entries = Object.entries(parsed)
        .filter(([key, value]) => key && value && typeof value === 'object')
        .map(([key, value]) => {
          const estatus = value.estatus == null ? '' : String(value.estatus).trim();
          const fechaCancelacion = value.fechaCancelacion == null ? '' : String(value.fechaCancelacion).trim();
          return [key, { estatus, fechaCancelacion }];
        });
      nuevoEstatusStickyValuesRef.current = new Map(entries);
    } catch (err) {
      console.warn('No se pudo restaurar el historial de Nuevo estatus:', err);
    }
  }, []);

  const usuario = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('usuario') || '{}');
    } catch (err) {
      console.warn('No se pudo leer el usuario desde localStorage:', err);
      return {};
    }
  }, []);
  const userRoles = useMemo(() => getUserRoles(usuario), [usuario]);
  const esAdministrador = userRoles.includes('administrador') || userRoles.includes('administradores');
  const esSupervisor = userRoles.includes('supervisor');
  const esCaptura = userRoles.includes('captura');
  const esSeguimientos = userRoles.includes('seguimientos');
  const esFacturacion = userRoles.includes('facturacion');
  const esCancelaciones = userRoles.includes('cancelaciones');
  const esCancelacion = userRoles.includes('cancelacion');
  const puedeEditarPedidoMultiasistencia = esSupervisor || esCaptura;
  const puedeEditarEstatusPago = esAdministrador || esSupervisor || esFacturacion;
  const puedeEditarEstatusCancelacionMultiasistencia = esAdministrador || esSupervisor || esCancelaciones || esCancelacion;
  const puedeGestionarNuevoEstatus = esSupervisor || esSeguimientos || esFacturacion || esCancelaciones;
  const puedeEditarPortalQ = esSupervisor || userRoles.includes('supervisores') || esFacturacion || esCancelaciones || esCancelacion || esCaptura;
  const puedeEditarPrecioMultiasistencia = puedeEditarPortalQ;
  const esChoferBackOrder = ['chofer', 'choferes'].includes(String(usuario?.role || '').trim().toLowerCase());
  const usuarioNombre = useMemo(() => {
    const raw = usuario?.username || usuario?.usuario || usuario?.nombreUsuario || usuario?.nombre || '';
    return raw ? String(raw).trim() : '';
  }, [usuario]);
  const canEditPedidoForRow = useCallback((rowData) => (
    puedeEditarPedidoMultiasistencia && (isMultiasistenciaAseguradora(rowData?.ASEGURADORA) || isQualitasXnuupAseguradora(rowData?.ASEGURADORA))
  ), [puedeEditarPedidoMultiasistencia]);

  const canEditBackOrderQualitasForRow = useCallback((rowData) => (
    esChoferBackOrder === false && isQualitasXnuupAseguradora(rowData?.ASEGURADORA)
  ), [esChoferBackOrder]);

  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === 'undefined') return 'principal';
    const savedTab = localStorage.getItem('baseDatosTab') || 'principal';
    if (savedTab === 'ordenes' || savedTab === 'pagos') return 'principal';
    return savedTab;
  });
  const [baseDataRaw, setBaseDataRaw] = useState([]);
  const [nuevoEstatusData, setNuevoEstatusData] = useState([]);
  const [ordenesData, setOrdenesData] = useState([]);
  const [, setPagosData] = useState([]);
  const [principalRowDeletingId, setPrincipalRowDeletingId] = useState(null);
  const [selectedCount, setSelectedCount] = useState(0);
  const [nuevoEstatusSelectedCount, setNuevoEstatusSelectedCount] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [searchType, setSearchType] = useState('contiene');
  const [secondarySearchText, setSecondarySearchText] = useState('');
  const [secondarySearchType, setSecondarySearchType] = useState('contiene');
  const [tertiarySearchText, setTertiarySearchText] = useState('');
  const [tertiarySearchType, setTertiarySearchType] = useState('contiene');
  const [principalPage, setPrincipalPage] = useState(1);
  const [principalPageSize, setPrincipalPageSize] = useState(300);
  const [principalTotalRows, setPrincipalTotalRows] = useState(0);
  const [principalTotalPages, setPrincipalTotalPages] = useState(1);
  const [principalLoading, setPrincipalLoading] = useState(false);
  const [goToPageInput, setGoToPageInput] = useState('1');
  const [nuevoEstatusPage, setNuevoEstatusPage] = useState(1);
  const [nuevoEstatusPageSize, setNuevoEstatusPageSize] = useState(300);
  const [nuevoEstatusTotalPages, setNuevoEstatusTotalPages] = useState(1);
  const [nuevoEstatusGoToPageInput, setNuevoEstatusGoToPageInput] = useState('1');
  const [counterNow, setCounterNow] = useState(() => Date.now());
  const [searchNuevoText, setSearchNuevoText] = useState('');
  const [nuevoCancelacionStartDate, setNuevoCancelacionStartDate] = useState('');
  const [nuevoCancelacionEndDate, setNuevoCancelacionEndDate] = useState('');
  const [nuevoFechaFilterField, setNuevoFechaFilterField] = useState('FECHA_CANCELACION_DE_PIEZA');
  const [showColumnListPrincipal, setShowColumnListPrincipal] = useState(false);
  const [showColumnListNuevo, setShowColumnListNuevo] = useState(false);
  const [cellEditorDialog, setCellEditorDialog] = useState(null);
  const [canUndo, setCanUndo] = useState(false);

  const [adjuntos, setAdjuntos] = useState([]);
  const [adjuntosError, setAdjuntosError] = useState('');
  const [adjuntosLoading, setAdjuntosLoading] = useState(false);
  const [adjuntosUploading, setAdjuntosUploading] = useState(false);
  const [adjuntosReloadKey, setAdjuntosReloadKey] = useState(0);
  const [previewAdjunto, setPreviewAdjunto] = useState(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewOffset, setPreviewOffset] = useState({ x: 0, y: 0 });
  const [previewPdfPages, setPreviewPdfPages] = useState(0);
  const [previewPdfCurrentPage, setPreviewPdfCurrentPage] = useState(1);
  const [previewPdfLoadError, setPreviewPdfLoadError] = useState('');
  const previewViewportRef = useRef(null);
  const previewGestureRef = useRef({
    isDragging: false,
    dragStartX: 0,
    dragStartY: 0,
    dragStartOffsetX: 0,
    dragStartOffsetY: 0,
    touchMode: null,
    pinchStartDistance: 0,
    pinchStartZoom: 1,
    pinchStartOffsetX: 0,
    pinchStartOffsetY: 0,
  });
  const [rowQuickView, setRowQuickView] = useState(null);
  const [rowQuickViewAdjuntosLoading, setRowQuickViewAdjuntosLoading] = useState(false);
  const [rowQuickViewAdjuntosCount, setRowQuickViewAdjuntosCount] = useState(null);
  const [rowQuickViewAdjuntosError, setRowQuickViewAdjuntosError] = useState('');
  const [rowQuickViewSavingField, setRowQuickViewSavingField] = useState('');
  const [rowQuickViewEditError, setRowQuickViewEditError] = useState('');

  const [isAssigningLocalidades, setIsAssigningLocalidades] = useState(false);
  const [nuevoEstatusExcelData, setNuevoEstatusExcelData] = useState([]);
  const [nuevoEstatusPuedeCargar, setNuevoEstatusPuedeCargar] = useState(false);
  const [nuevoEstatusIsUploading, setNuevoEstatusIsUploading] = useState(false);
  const [nuevoEstatusLastUpdated, setNuevoEstatusLastUpdated] = useState(null);

  const nuevoEstatusLastUpdatedLabel = useMemo(() => {
    if (!nuevoEstatusLastUpdated) return '';
    const parsed = new Date(nuevoEstatusLastUpdated);
    if (Number.isNaN(parsed.getTime())) return String(nuevoEstatusLastUpdated);
    return parsed.toLocaleString('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'medium'
    });
  }, [nuevoEstatusLastUpdated]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setCounterNow(Date.now());
    }, 60 * 1000);
    return () => window.clearInterval(timerId);
  }, []);

  const markSelfUpdate = useCallback(() => {
    selfUpdateTimestampRef.current = Date.now();
  }, []);

  const pushUndoEntry = useCallback((entry) => {
    if (!entry) return;
    const stack = undoStackRef.current;
    stack.push(entry);
    while (stack.length > 50) {
      stack.shift();
    }
    setCanUndo(stack.length > 0);
  }, []);

  const restoreUndoEntry = useCallback(() => {
    const entry = pendingUndoRef.current;
    if (!entry) {
      suppressHistoryRef.current = false;
      return;
    }
    const stack = undoStackRef.current;
    stack.push(entry);
    while (stack.length > 50) {
      stack.shift();
    }
    pendingUndoRef.current = null;
    suppressHistoryRef.current = false;
    setCanUndo(stack.length > 0);
  }, []);

  const updateBaseRowValue = useCallback((rowId, field, rawValue, rowKey = null) => {
    if (!field) return;
    const normalized = rawValue == null ? '' : String(rawValue);
    setBaseDataRaw(prev => {
      if (!Array.isArray(prev) || !prev.length) return prev;
      const matchIndex = prev.findIndex(row => {
        if (!row) return false;
        if (rowId != null && row.id != null && Number(row.id) === Number(rowId)) return true;
        if (rowKey) {
          const key = buildPedidoItemKey(row.PEDIDO, row.ITEM);
          if (key && key === rowKey) return true;
        }
        return false;
      });
      if (matchIndex < 0) return prev;
      const currentRow = prev[matchIndex];
      const currentValue = currentRow?.[field];
      if ((currentValue == null ? '' : String(currentValue)) === normalized) {
        return prev;
      }
      const next = [...prev];
      next[matchIndex] = { ...currentRow, [field]: normalized };
      return next;
    });
  }, []);

  const getCellChangeSuppressionKey = useCallback((node, field, dataOverride = null) => {
    if (!field) return '';
    const data = dataOverride || node?.data || {};
    const rowNodeId = node?.id == null ? '' : String(node.id);
    const dataId = data?.id == null ? '' : String(data.id);
    const pedido = data?.PEDIDO == null ? '' : String(data.PEDIDO);
    const item = data?.ITEM == null ? '' : String(data.ITEM);
    return `${String(field)}|||${rowNodeId}|||${dataId}|||${pedido}|||${item}`;
  }, []);

  const handleDeletePrincipalRow = useCallback(async (row) => {
    if (!esSupervisor) return;
    const numericId = Number(row?.id);
    if (!Number.isInteger(numericId) || numericId <= 0) {
      alert('No se pudo identificar la fila a borrar.');
      return;
    }
    const confirmed = window.confirm('¿Seguro que deseas borrar esta fila completa? Esta acción no se puede deshacer.');
    if (!confirmed) return;
    setPrincipalRowDeletingId(numericId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/basedatos/borrar`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [numericId] })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.mensaje || payload?.error || 'No se pudo borrar el registro.');
      }
      setBaseDataRaw(prev => prev.filter(current => Number(current?.id) !== numericId));
      markSelfUpdate();
    } catch (err) {
      console.error('Error al borrar registro de la base de datos:', err);
      alert(err.message || 'No se pudo borrar el registro.');
    } finally {
      setPrincipalRowDeletingId(current => (current === numericId ? null : current));
    }
  }, [esSupervisor, markSelfUpdate]);

  const renderDeleteCell = useCallback((params) => {
    const numericId = Number(params?.data?.id);
    if (!Number.isInteger(numericId) || numericId <= 0) {
      return '';
    }
    const isDeleting = principalRowDeletingId === numericId;
    return (
      <button
        type="button"
        onClick={() => handleDeletePrincipalRow(params.data)}
        disabled={isDeleting}
        style={{
          padding: '2px 8px',
          borderRadius: 6,
          fontSize: 12,
          border: '1px solid #dc2626',
          background: isDeleting ? '#fecaca' : '#fee2e2',
          color: '#b91c1c',
          cursor: isDeleting ? 'not-allowed' : 'pointer'
        }}
      >
        {isDeleting ? 'Borrando...' : 'Borrar'}
      </button>
    );
  }, [handleDeletePrincipalRow, principalRowDeletingId]);

  const baseColumnDefsWithPermissions = useMemo(
    () => baseDatosColumnDefs.map(column => {
      if (column.field === 'LOCALIDAD') {
        return {
          ...column,
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: ALLOWED_LOCALIDADES
          }
        };
      }
      if (column.field === 'COMPAQ') {
        return {
          ...column,
          editable: () => column.field === 'OC' ? (esSupervisor || esCaptura) : esSupervisor,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: COMPAQ_OPTIONS
          }
        };
      }
      if (column.field === 'PEDIDO') {
        return {
          ...column,
          editable: (params) => canEditPedidoForRow(params?.data),
          cellEditor: column.cellEditor || 'agTextCellEditor'
        };
      }
      if (column.field === 'ESTATUS_DE_PAGO') {
        return {
          ...column,
          editable: () => puedeEditarEstatusPago,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: ESTATUS_DE_PAGO_OPTIONS
          }
        };
      }
      if (column.field === 'NUEVO_ESTATUS') {
        return { ...column, editable: params => canEditNuevoEstatusForRow(params?.data) || puedeGestionarNuevoEstatus, cellEditor: column.cellEditor || 'agTextCellEditor' };
      }
      if (column.field === 'ESTATUS2') {
        return {
          ...column,
          editable: () => puedeGestionarNuevoEstatus,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: ESTATUS2_OPTIONS
          }
        };
      }
      if (column.field === 'DIAS_ENTREGADO1') {
        return {
          ...column,
          editable: () => puedeEditarEstatusCancelacionMultiasistencia,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: ESTATUS_CANCELACION_OPTIONS
          }
        };
      }
      if (column.field === 'BACK_ORDER') {
        return { ...column, editable: params => canEditBackOrderQualitasForRow(params?.data), cellEditor: column.cellEditor || 'agTextCellEditor' };
      }
      if (column.field && ESTATUS_EDITABLE_FIELDS.has(column.field)) {
        return {
          ...column,
          editable: () => puedeGestionarNuevoEstatus,
          cellEditor: column.cellEditor || 'agTextCellEditor'
        };
      }
      if (column.field === 'PRECIO') return { ...column, editable: params => puedeEditarPrecioMultiasistencia && isMultiasistenciaAseguradora(params?.data?.ASEGURADORA), cellEditor: column.cellEditor || 'agTextCellEditor' };
      if (column.field === 'ESTATUS_PORTAL_Q') {
        return { ...column, editable: params => puedeEditarPortalQ && isQualitasXnuupAseguradora(params?.data?.ASEGURADORA), cellEditor: column.cellEditor || 'agTextCellEditor' };
      }
      if (column.field === 'FECHA_CANCELACION_DE_PIEZA') {
        return {
          ...column,
          editable: params => (esSupervisor && isMultiasistenciaAseguradora(params?.data?.ASEGURADORA)) || ((esSupervisor || esCaptura) && isQualitasBackOrderRCanceladoRow(params?.data)) || (puedeEditarPortalQ && isQualitasXnuupAseguradora(params?.data?.ASEGURADORA)),
          cellEditor: column.cellEditor || 'agTextCellEditor'
        };
      }
      if (
        column.field
        && (
          CAPTURA_EDITABLE_FIELDS.has(column.field)
          || column.field === 'FECHA_CANCELACION_DE_PIEZA'
        )
      ) {
        return {
          ...column,
          editable: () => column.field === 'OC' ? (esSupervisor || esCaptura) : esSupervisor,
          cellEditor: column.cellEditor || 'agTextCellEditor'
        };
      }
      return { ...column };
    }),
    [canEditBackOrderQualitasForRow, canEditPedidoForRow, esCaptura, esSupervisor, puedeEditarEstatusCancelacionMultiasistencia, puedeEditarEstatusPago, puedeGestionarNuevoEstatus, puedeEditarPortalQ, puedeEditarPrecioMultiasistencia]
  );

  const columnDefs = useMemo(() => {
    if (!esSupervisor) {
      return baseColumnDefsWithPermissions;
    }
    if (!Array.isArray(baseColumnDefsWithPermissions) || baseColumnDefsWithPermissions.length === 0) {
      return baseColumnDefsWithPermissions;
    }
    const deleteColumn = {
      headerName: '',
      field: '__acciones',
      colId: '__acciones',
      width: 110,
      minWidth: 90,
      maxWidth: 130,
      suppressMenu: true,
      suppressMovable: true,
      suppressSizeToFit: true,
      sortable: false,
      filter: false,
      cellRenderer: renderDeleteCell
    };
    return [...baseColumnDefsWithPermissions, deleteColumn];
  }, [baseColumnDefsWithPermissions, esSupervisor, renderDeleteCell]);

  const canEditField = useCallback((field, rowData = null) => {
    if (!field) return false;
    if (field === 'PEDIDO') return canEditPedidoForRow(rowData);
    if (field === 'LOCALIDAD') return true;
    if (field === 'ESTATUS_DE_PAGO') return puedeEditarEstatusPago;
    if (field === 'DIAS_ENTREGADO1') {
      return puedeEditarEstatusCancelacionMultiasistencia;
    }
    if (field === 'NUEVO_ESTATUS') return canEditNuevoEstatusForRow(rowData) || puedeGestionarNuevoEstatus;
    if (field === 'BACK_ORDER') return canEditBackOrderQualitasForRow(rowData);
    if (ESTATUS_EDITABLE_FIELDS.has(field)) return puedeGestionarNuevoEstatus;
    if (field === 'PRECIO') return puedeEditarPrecioMultiasistencia && isMultiasistenciaAseguradora(rowData?.ASEGURADORA);
    if (field === 'ESTATUS_PORTAL_Q') return puedeEditarPortalQ && isQualitasXnuupAseguradora(rowData?.ASEGURADORA);
    if (field === 'FECHA_CANCELACION_DE_PIEZA') return ((esSupervisor || esCaptura) && isMultiasistenciaAseguradora(rowData?.ASEGURADORA)) || ((esSupervisor || esCaptura) && isQualitasBackOrderRCanceladoRow(rowData)) || (puedeEditarPortalQ && isQualitasXnuupAseguradora(rowData?.ASEGURADORA));
    if (CAPTURA_EDITABLE_FIELDS.has(field)) return field === 'OC' ? (esSupervisor || esCaptura) : esSupervisor;
    return false;
  }, [canEditBackOrderQualitasForRow, canEditPedidoForRow, esCaptura, esSupervisor, puedeEditarEstatusCancelacionMultiasistencia, puedeEditarEstatusPago, puedeGestionarNuevoEstatus, puedeEditarPortalQ, puedeEditarPrecioMultiasistencia]);

  const scheduleLivePersist = useCallback((key, persistFn) => {
    const timers = liveUpdateTimersRef.current;
    const existing = timers.get(key);
    if (existing) {
      clearTimeout(existing.timeoutId);
    }
    const timeoutId = setTimeout(() => {
      timers.delete(key);
      persistFn().catch(err => {
        console.error('Error al guardar un cambio en vivo:', err);
      });
    }, LIVE_UPDATE_DELAY_MS);
    timers.set(key, { timeoutId, persistFn });
  }, []);

  const cancelLivePersist = useCallback((key) => {
    const timers = liveUpdateTimersRef.current;
    const stored = timers.get(key);
    if (!stored) return;
    clearTimeout(stored.timeoutId);
    timers.delete(key);
  }, []);

  const flushLivePersist = useCallback((key) => {
    const timers = liveUpdateTimersRef.current;
    const stored = timers.get(key);
    if (!stored) return;
    clearTimeout(stored.timeoutId);
    timers.delete(key);
    return stored.persistFn();
  }, []);
  const baseColumnFields = useMemo(
    () => columnDefs
      .map(col => col.field)
      .filter(field => {
        if (!field) return false;
        if (field === 'checked') return false;
        if (typeof field === 'string' && field.startsWith('__')) return false;
        return true;
      }),
    [columnDefs]
  );
  const columnLabels = useMemo(() => {
    const labels = {};
    columnDefs.forEach(col => {
      if (col.field && col.field !== 'checked' && !(typeof col.field === 'string' && col.field.startsWith('__'))) {
        labels[col.field] = col.headerName || col.field;
      }
    });
    return labels;
  }, [columnDefs]);
  const headerLookup = useMemo(() => {
    const map = new Map();
    baseColumnFields.forEach(field => {
      map.set(normalizeHeaderKey(field), field);
    });
    return map;
  }, [baseColumnFields]);
  const defaultColumnVisibility = useMemo(() => {
    const defaults = {};
    baseColumnFields.forEach(field => {
      defaults[field] = true;
    });
    return defaults;
  }, [baseColumnFields]);

  const applySearchFilter = useCallback((rows) => rows, []);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnVisibilityLoaded, setColumnVisibilityLoaded] = useState(false);

  const ordenesLookup = useMemo(() => {
    const map = new Map();
    ordenesData.forEach(entry => {
      if (!entry) return;
      const pedidoKey = normalizeKeyPart(entry.PEDIDO);
      if (!pedidoKey || map.has(pedidoKey)) return;
      const rawOrden = entry.ORDEN_PROVEEDOR;
      const normalizedOrden = rawOrden == null ? '' : String(rawOrden);
      map.set(pedidoKey, normalizedOrden);
    });
    return map;
  }, [ordenesData]);

  // Build a quick lookup so we can project fields from Nuevo estatus into the main grid.
  const nuevoEstatusLookup = useMemo(() => {
    const map = new Map();

    nuevoEstatusData.forEach(row => {
      if (!row || isQualitasNuevoEstatusRow(row)) return;

      const key = buildPedidoItemKey(row.PEDIDO, row.ITEM);
      if (!key || map.has(key)) return;

      const estatus = row.ESTATUS == null ? '' : String(row.ESTATUS);
      const fechaCancelacion = row.FECHA_CANCELACION_DE_PIEZA == null
        ? ''
        : String(row.FECHA_CANCELACION_DE_PIEZA);

      if (!estatus.trim() && !fechaCancelacion.trim()) return;

      map.set(key, { estatus, fechaCancelacion });
    });

    baseDataRaw.forEach(baseRow => {
      if (!baseRow || !isQualitasNuevoEstatusRow(baseRow)) return;

      const matched = resolveQualitasNuevoEstatusRow(
        baseRow,
        nuevoEstatusData,
        baseDataRaw
      );

      if (!matched) return;

      const key = buildQualitasNuevoEstatusProjectionKey(baseRow);
      if (!key || map.has(key)) return;

      const estatus = matched.ESTATUS == null ? '' : String(matched.ESTATUS);
      const fechaCancelacion = matched.FECHA_CANCELACION_DE_PIEZA == null
        ? ''
        : String(matched.FECHA_CANCELACION_DE_PIEZA);

      if (!estatus.trim() && !fechaCancelacion.trim()) return;

      map.set(key, { estatus, fechaCancelacion });
    });

    return map;
  }, [baseDataRaw, nuevoEstatusData]);

  useEffect(() => {
    const stickyMap = nuevoEstatusStickyValuesRef.current;
    let mapChanged = false;
    nuevoEstatusData.forEach((row) => {
      if (!row || isQualitasNuevoEstatusRow(row)) return;
      const key = buildPedidoItemKey(row.PEDIDO, row.ITEM);
      if (!key) return;

      const prev = stickyMap.get(key) || { estatus: '', fechaCancelacion: '' };
      const estatus = row.ESTATUS == null ? '' : String(row.ESTATUS).trim();
      const fechaCancelacion = row.FECHA_CANCELACION_DE_PIEZA == null ? '' : String(row.FECHA_CANCELACION_DE_PIEZA).trim();

      let changed = false;
      const next = { ...prev };
      if (estatus) {
        next.estatus = estatus;
        changed = true;
      }
      if (fechaCancelacion) {
        next.fechaCancelacion = fechaCancelacion;
        changed = true;
      }

      if (changed) {
        stickyMap.set(key, next);
        mapChanged = true;
      }
    });
    if (mapChanged) {
      persistNuevoEstatusStickyMap();
    }
  }, [nuevoEstatusData, persistNuevoEstatusStickyMap]);

  useEffect(() => {
    const stickyMap = nuevoEstatusStickyValuesRef.current;
    let mapChanged = false;
    baseDataRaw.forEach((row) => {
      if (!row || isQualitasNuevoEstatusRow(row)) return;
      const key = buildPedidoItemKey(row.PEDIDO, row.ITEM);
      if (!key) return;

      const prev = stickyMap.get(key) || { estatus: '', fechaCancelacion: '' };
      const estatus = row.NUEVO_ESTATUS == null ? '' : String(row.NUEVO_ESTATUS).trim();
      const fechaCancelacion = row.FECHA_CANCELACION_DE_PIEZA == null ? '' : String(row.FECHA_CANCELACION_DE_PIEZA).trim();

      let changed = false;
      const next = { ...prev };
      if (estatus) {
        next.estatus = estatus;
        changed = true;
      }
      if (fechaCancelacion) {
        next.fechaCancelacion = fechaCancelacion;
        changed = true;
      }

      if (changed) {
        stickyMap.set(key, next);
        mapChanged = true;
      }
    });
    if (mapChanged) {
      persistNuevoEstatusStickyMap();
    }
  }, [baseDataRaw, persistNuevoEstatusStickyMap]);

  // Inject matched values from Nuevo estatus into the principal grid when we render.
  const principalRowData = useMemo(() => {
    if (!Array.isArray(baseDataRaw) || baseDataRaw.length === 0) return baseDataRaw;
    const nowDate = new Date(counterNow);
    return baseDataRaw.map(row => {
      if (!row) return row;
      let nextRow = row;

      const esQualitasOverlay = isQualitasNuevoEstatusRow(row);
      const key = esQualitasOverlay
        ? buildQualitasNuevoEstatusProjectionKey(row)
        : buildPedidoItemKey(row.PEDIDO, row.ITEM);

      if (key) {
        const overlay = nuevoEstatusLookup.get(key);
        const stickyOverlay = esQualitasOverlay
          ? null
          : nuevoEstatusStickyValuesRef.current.get(key);
        const overlayEstatus = overlay?.estatus == null ? '' : String(overlay.estatus).trim();
        const stickyEstatus = stickyOverlay?.estatus == null ? '' : String(stickyOverlay.estatus).trim();
        const estatusToApply = isMultiasistenciaAseguradora(row?.ASEGURADORA) ? '' : (overlayEstatus || stickyEstatus);

        if (estatusToApply !== '') {
          const currentRaw = row.NUEVO_ESTATUS == null ? '' : String(row.NUEVO_ESTATUS);
          if (currentRaw !== estatusToApply) {
            nextRow = nextRow === row ? { ...row } : nextRow;
            nextRow.NUEVO_ESTATUS = estatusToApply;
          }
        }

      }

      const pedidoKey = normalizeKeyPart(row.PEDIDO);
      if (pedidoKey && ordenesLookup.has(pedidoKey)) {
        const ocOverlay = ordenesLookup.get(pedidoKey) ?? '';
        const currentOc = row.OC == null ? '' : String(row.OC);
        if (currentOc !== ocOverlay) {
          nextRow = nextRow === row ? { ...nextRow } : nextRow;
          nextRow.OC = ocOverlay;
        }
      }

      const statusCancelacion = normalizeCancelacionStatus(nextRow.DIAS_ENTREGADO1);
      const currentDiasRaw = nextRow.DIAS_ENTREGADO2 == null ? '' : String(nextRow.DIAS_ENTREGADO2);
      const computedDias = buildDiasSinRecuperarLabel(nextRow.BACK_ORDER, nextRow.FECHA_CANCELACION_DE_PIEZA, nowDate);
      let nextDias = currentDiasRaw;

      if (ESTATUS_CANCELACION_PAUSA.has(statusCancelacion)) {
        nextDias = '0 dias';
      } else if (isRecoveringCancelacionStatus(statusCancelacion)) {
        nextDias = computedDias;
      } else {
        nextDias = '';
      }

      if ((nextDias == null ? '' : String(nextDias)) !== currentDiasRaw) {
        nextRow = nextRow === row ? { ...nextRow } : nextRow;
        nextRow.DIAS_ENTREGADO2 = nextDias;
      }

      return nextRow;
    });
  }, [baseDataRaw, counterNow, nuevoEstatusLookup, ordenesLookup]);

  const siniestroItemDuplicateSet = useMemo(() => {
    if (!Array.isArray(principalRowData) || principalRowData.length === 0) {
      return new Set();
    }
    const counts = new Map();
    principalRowData.forEach(row => {
      const key = buildSiniestroItemKey(row?.SINIESTRO, row?.ITEM);
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    const duplicates = new Set();
    counts.forEach((count, key) => {
      if (count > 1) duplicates.add(key);
    });
    return duplicates;
  }, [principalRowData]);

  const getPrincipalRowClass = useCallback((params) => {
    const data = params?.data;
    if (!data) return '';
    const key = buildSiniestroItemKey(data.SINIESTRO, data.ITEM);
    return key && siniestroItemDuplicateSet.has(key) ? 'row-texto-rojo' : '';
  }, [siniestroItemDuplicateSet]);

  useEffect(() => {
    const storedRaw = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    if (storedRaw) {
      try {
        const stored = JSON.parse(storedRaw);
        setColumnVisibility({ ...defaultColumnVisibility, ...stored });
        setColumnVisibilityLoaded(true);
        return;
      } catch (err) {
        console.warn('No se pudo leer la visibilidad guardada:', err);
      }
    }
    setColumnVisibility(defaultColumnVisibility);
    setColumnVisibilityLoaded(true);
  }, [defaultColumnVisibility]);

  useEffect(() => {
    if (!columnVisibilityLoaded) return;
    localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(columnVisibility));
  }, [columnVisibility, columnVisibilityLoaded]);

  useEffect(() => {
    if (!columnVisibilityLoaded) return;
    setColumnVisibility(prev => {
      const next = { ...prev };
      let changed = false;
      baseColumnFields.forEach(field => {
        if (typeof next[field] === 'undefined') {
          next[field] = true;
          changed = true;
        }
      });
      Object.keys(next).forEach(field => {
        if (!baseColumnFields.includes(field)) {
          delete next[field];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [baseColumnFields, columnVisibilityLoaded]);

  const handleToggleColumnVisibility = useCallback((field) => {
    if (!columnVisibilityLoaded) return;
    setColumnVisibility(prev => {
      const currentlyVisible = prev[field] !== false;
      return { ...prev, [field]: !currentlyVisible };
    });
  }, [columnVisibilityLoaded]);

  const filteredColumnDefs = useMemo(() => {
    if (!columnVisibilityLoaded) return columnDefs;
    return columnDefs.filter(col => {
      if (!col.field || col.field === 'checked') return true;
      return columnVisibility[col.field] !== false;
    });
  }, [columnDefs, columnVisibility, columnVisibilityLoaded]);

  const getPrincipalRowId = useCallback((params) => {
    const rawId = params?.data?.id;
    if (rawId === null || rawId === undefined) {
      const pedido = params?.data?.PEDIDO;
      const item = params?.data?.ITEM;
      return `principal-${pedido ?? 'sin-id'}-item-${item ?? 'na'}`;
    }
    return `principal-${rawId}`;
  }, []);

  const getNuevoEstatusRowId = useCallback((params) => {
    const rawId = params?.data?.id;
    if (rawId === null || rawId === undefined) {
      const pedido = params?.data?.PEDIDO;
      const item = params?.data?.ITEM;
      return `nuevo-${pedido ?? 'sin-id'}-item-${item ?? 'na'}-${params?.node?.rowIndex ?? 0}`;
    }
    return `nuevo-${rawId}`;
  }, []);

  const cargarDatos = useCallback(() => {
    const params = new URLSearchParams({
      page: String(principalPage),
      pageSize: String(principalPageSize),
      searchText,
      searchType,
      secondarySearchText,
      secondarySearchType,
      tertiarySearchText,
      tertiarySearchType
    });

    setPrincipalLoading(true);
    fetch(`${API_BASE_URL}/api/basedatos/obtener?${params.toString()}`)
      .then(res => res.json())
      .then(payload => {
        const rows = Array.isArray(payload?.rows) ? payload.rows : (Array.isArray(payload) ? payload : []);
        const total = Number(payload?.total);
        const totalPages = Number(payload?.totalPages);
        const currentPage = Number(payload?.page);

        setBaseDataRaw(rows);
        setPrincipalTotalRows(Number.isFinite(total) ? total : rows.length);
        setPrincipalTotalPages(Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1);
        if (Number.isFinite(currentPage) && currentPage > 0) {
          setPrincipalPage(currentPage);
        }
      })
      .catch((error) => {
        console.error('No se pudieron cargar datos paginados:', error);
        setBaseDataRaw([]);
        setPrincipalTotalRows(0);
        setPrincipalTotalPages(1);
      })
      .finally(() => {
        setPrincipalLoading(false);
      });
  }, [principalPage, principalPageSize, searchText, searchType, secondarySearchText, secondarySearchType, tertiarySearchText, tertiarySearchType]);

  useEffect(() => {
    setPrincipalPage(1);
  }, [searchText, searchType, secondarySearchText, secondarySearchType, tertiarySearchText, tertiarySearchType]);

  useEffect(() => {
    setGoToPageInput(String(principalPage));
  }, [principalPage]);

  const cargarNuevoEstatus = useCallback(() => {
    fetch(`${API_BASE_URL}/api/nuevo-estatus/obtener`)
      .then(res => res.json())
      .then(payload => {
        const rows = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.rows)
            ? payload.rows
            : [];

        const sorted = [...rows].sort((a, b) => {
          const fechaA = parseFechaPedido(a.FECHA_PEDIDO);
          const fechaB = parseFechaPedido(b.FECHA_PEDIDO);
          return fechaB - fechaA;
        });

        setNuevoEstatusData(sorted);

  const lastUpdatedValue = typeof payload?.lastUpdated === 'string' ? payload.lastUpdated : null;
  setNuevoEstatusLastUpdated(lastUpdatedValue || null);
      })
      .catch(() => {
        setNuevoEstatusData([]);
      });
  }, []);

  const cargarOrdenes = useCallback(() => {
    fetch(`${API_BASE_URL}/api/basedatos/ordenes-proveedor/obtener`)
      .then(res => res.json())
        .then(data => {
          if (!Array.isArray(data)) {
            setOrdenesData([]);
            return;
          }
          setOrdenesData(data);
        })
      .catch(() => setOrdenesData([]));
  }, []);

  const cargarPagos = useCallback(() => {
    fetch(`${API_BASE_URL}/api/pagos/obtener`)
      .then(res => res.json())
      .then(data => {
        if (!Array.isArray(data)) {
          setPagosData([]);
          return;
        }
        setPagosData(data);
      })
      .catch(() => setPagosData([]));
  }, []);

  useEffect(() => {
    cargarDatos();
    cargarOrdenes();
    cargarNuevoEstatus();
    cargarPagos();
    const refrescar = () => cargarDatos();
    const refrescarOrdenes = () => cargarOrdenes();
    const refrescarNuevoEstatus = () => cargarNuevoEstatus();
    const refrescarPagos = () => cargarPagos();
    window.addEventListener('refreshBaseDatos', refrescar);
    window.addEventListener('refreshOrdenesProveedor', refrescarOrdenes);
    window.addEventListener('refreshNuevoEstatus', refrescarNuevoEstatus);
    window.addEventListener('refreshPagos', refrescarPagos);

    // --- SOCKET.IO ---
    const socket = socketIOClient(SOCKET_URL);
    socket.on('excel_data_updated', () => {
      if (Date.now() - selfUpdateTimestampRef.current < SELF_UPDATE_SUPPRESSION_MS) {
        return;
      }
      cargarDatos();
      cargarOrdenes();
      cargarNuevoEstatus();
      cargarPagos();
    });
    socket.on('nuevo_estatus_updated', (payload) => {
      if (Date.now() - selfUpdateTimestampRef.current < SELF_UPDATE_SUPPRESSION_MS) {
        return;
      }
      if (payload && typeof payload.lastUpdated === 'string') {
        setNuevoEstatusLastUpdated(payload.lastUpdated);
      }
      cargarNuevoEstatus();
      cargarDatos();
    });
    socket.on('pagos_updated', () => {
      if (Date.now() - selfUpdateTimestampRef.current < SELF_UPDATE_SUPPRESSION_MS) {
        return;
      }
      cargarPagos();
    });

    return () => {
      window.removeEventListener('refreshBaseDatos', refrescar);
      window.removeEventListener('refreshOrdenesProveedor', refrescarOrdenes);
      window.removeEventListener('refreshNuevoEstatus', refrescarNuevoEstatus);
      window.removeEventListener('refreshPagos', refrescarPagos);
      socket.disconnect();
    };
  }, [cargarDatos, cargarOrdenes, cargarNuevoEstatus, cargarPagos]);

  useEffect(() => {
    localStorage.setItem('baseDatosTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'ordenes' || activeTab === 'pagos') {
      setActiveTab('principal');
    }
  }, [activeTab]);

  const filteredDataMemo = useMemo(() => (
    applySearchFilter(principalRowData)
  ), [principalRowData, applySearchFilter]);

  const filteredNuevoEstatus = useMemo(() => {
    let result = nuevoEstatusData;
    if (searchNuevoText) {
      const lower = searchNuevoText.toLowerCase();
      result = result.filter(row =>
        Object.values(row).some(val => {
          if (typeof val !== 'string' && typeof val !== 'number') return false;
          return String(val).toLowerCase().includes(lower);
        })
      );
    }
    const startDate = parseCalendarInputDate(nuevoCancelacionStartDate, false);
    const endDate = parseCalendarInputDate(nuevoCancelacionEndDate, true);
    const selectedDateField = nuevoFechaFilterField || 'FECHA_CANCELACION_DE_PIEZA';
    if (startDate || endDate) {
      result = result.filter(row => {
        const cellDate = parseDateValue(row?.[selectedDateField]);
        if (!cellDate || Number.isNaN(cellDate.getTime())) return false;
        if (startDate && cellDate < startDate) return false;
        if (endDate && cellDate > endDate) return false;
        return true;
      });
    }
    const nowDate = new Date(counterNow);
    return result.map((row) => {
      if (!row) return row;
      const statusCancelacion = normalizeCancelacionStatus(row.DIAS_ENTREGADO1);
      const currentDiasRaw = row.DIAS_ENTREGADO2 == null ? '' : String(row.DIAS_ENTREGADO2);
      const computedDias = buildDiasSinRecuperarLabel(row.BACK_ORDER, row.FECHA_CANCELACION_DE_PIEZA, nowDate);
      let nextDias = currentDiasRaw;

      if (ESTATUS_CANCELACION_PAUSA.has(statusCancelacion)) {
        nextDias = '0 dias';
      } else if (isRecoveringCancelacionStatus(statusCancelacion)) {
        nextDias = computedDias;
      } else {
        nextDias = '';
      }

      if (currentDiasRaw !== nextDias) {
        return { ...row, DIAS_ENTREGADO2: nextDias };
      }
      return row;
    });
  }, [counterNow, nuevoEstatusData, searchNuevoText, nuevoCancelacionStartDate, nuevoCancelacionEndDate, nuevoFechaFilterField]);

  const updateNuevoEstatusPaginationState = useCallback(() => {
    const api = nuevoEstatusGridRef.current?.api;
    if (!api) return;
    const rawTotalPages = api.paginationGetTotalPages?.() || 0;
    const totalPages = Math.max(rawTotalPages, 1);
    const currentPage = (api.paginationGetCurrentPage?.() || 0) + 1;
    setNuevoEstatusTotalPages(totalPages);
    setNuevoEstatusPage(currentPage);
    setNuevoEstatusGoToPageInput(String(currentPage));
  }, []);

  useEffect(() => {
    if (activeTab !== 'nuevo-estatus') return;
    window.requestAnimationFrame(() => {
      updateNuevoEstatusPaginationState();
    });
  }, [activeTab, filteredNuevoEstatus, nuevoEstatusPageSize, updateNuevoEstatusPaginationState]);

  const onSelectionChanged = () => {
    if (gridRef.current) {
      const selectedRows = gridRef.current.api.getSelectedRows();
      setSelectedCount(selectedRows.length);
    }
  };

  const onNuevoEstatusSelectionChanged = () => {
    if (nuevoEstatusGridRef.current) {
      const selectedRows = nuevoEstatusGridRef.current.api.getSelectedRows();
      setNuevoEstatusSelectedCount(selectedRows.length);
    }
  };

  const processEditableCellChange = useCallback(async ({ field, newValue, oldValue, node, data, liveUpdate = false, onApplied }) => {
    if (!field || !node) return { success: false };

    const esCampoEstatus = ESTATUS_EDITABLE_FIELDS.has(field);
    const esEstatusPortalQ = field === 'ESTATUS_PORTAL_Q';
    const esFechaCancelacion = field === 'FECHA_CANCELACION_DE_PIEZA';
    const esCampoPortalQ = esEstatusPortalQ || esFechaCancelacion;
    const puedeEditarPortalQFila = puedeEditarPortalQ && isQualitasXnuupAseguradora(data?.ASEGURADORA);
    const puedeEditarPrecioFila = field === 'PRECIO' && puedeEditarPrecioMultiasistencia && isMultiasistenciaAseguradora(data?.ASEGURADORA);
    const puedeEditarBackOrder = field === 'BACK_ORDER' && canEditBackOrderQualitasForRow(data);
    const requiereSupervisor = CAPTURA_EDITABLE_FIELDS.has(field) && field !== 'OC';
    const puedeEditarOC = field === 'OC' && (esSupervisor || esCaptura);
    const puedeEditarPedido = field === 'PEDIDO' && canEditPedidoForRow(data);
    const rowKey = buildPedidoItemKey(data?.PEDIDO, data?.ITEM);
    const numericIdRaw = data?.id;
    const numericId = Number(numericIdRaw);
    const hasValidId = Number.isInteger(numericId);
    const skipHistory = suppressHistoryRef.current === true;

    const releaseSkip = () => {
      if (!skipHistory) return;
      suppressHistoryRef.current = false;
      pendingUndoRef.current = null;
      setCanUndo(undoStackRef.current.length > 0);
    };

    const normalizeValue = (raw) => {
      if (raw == null) return '';
      return typeof raw === 'string' ? raw : String(raw);
    };

    const previousValue = normalizeValue(oldValue);
    const incomingValue = normalizeValue(newValue);
    const currentGridValue = normalizeValue(node?.data?.[field]);

    const setNodeValue = (value) => {
      const normalized = normalizeValue(value);
      revertingRef.current = true;
      node.setDataValue(field, normalized);
      revertingRef.current = false;
      if (hasValidId || rowKey) {
        updateBaseRowValue(hasValidId ? numericId : null, field, normalized, rowKey);
      }
    };

    const notifyApplied = (value, meta) => {
      onApplied?.(normalizeValue(value), meta);
    };

    const applyGridValue = (value) => {
      setNodeValue(value);
      notifyApplied(value, { persisted: true });
    };

    const recordSuccess = (appliedValue) => {
      const normalizedApplied = normalizeValue(appliedValue);
      if (!skipHistory) {
        if (normalizedApplied !== previousValue) {
          pushUndoEntry({
            rowId: numericId,
            rowNodeId: node?.id ?? null,
            rowKey,
            field,
            previousValue,
            newValue: normalizedApplied
          });
        }
      } else {
        releaseSkip();
      }
    };

    const handleFailure = () => {
      if (!skipHistory) return;
      restoreUndoEntry();
      releaseSkip();
    };

    if (
      (esCampoEstatus && field !== 'NUEVO_ESTATUS' && field !== 'BACK_ORDER' && !puedeGestionarNuevoEstatus)
      || (field === 'NUEVO_ESTATUS' && !canEditNuevoEstatusForRow(data) && !puedeGestionarNuevoEstatus)
      || (requiereSupervisor && !esSupervisor && !(esFechaCancelacion && puedeEditarPortalQFila))
      || (field === 'OC' && !puedeEditarOC)
      || (field === 'BACK_ORDER' && !puedeEditarBackOrder)
      || (esEstatusPortalQ && !puedeEditarPortalQFila)
      || (field === 'PRECIO' && !puedeEditarPrecioFila)
    ) {
      setNodeValue(previousValue);
      notifyApplied(previousValue, { persisted: false, reverted: true });
      handleFailure();
      return { success: false };
    }

    if (field !== 'LOCALIDAD' && !esCampoEstatus && !esCampoPortalQ && !requiereSupervisor && !puedeEditarPedido && !puedeEditarOC && !puedeEditarPrecioFila) {
      if (field === 'BACK_ORDER') {
        notifyApplied(incomingValue, { persisted: true, system: true });
        releaseSkip();
        return { success: true, appliedValue: incomingValue };
      }
      setNodeValue(previousValue);
      notifyApplied(previousValue, { persisted: false, reverted: true });
      handleFailure();
      return { success: false };
    }

    if (!hasValidId) {
      setNodeValue(previousValue);
      notifyApplied(previousValue, { persisted: false, reverted: true });
      handleFailure();
      return { success: false };
    }

    const updateKey = `${numericId}:${field}`;

    if (field === 'LOCALIDAD') {
      const trimmed = incomingValue.trim();
      if (!trimmed) {
        setNodeValue(previousValue);
        notifyApplied(previousValue, { persisted: false, reverted: true });
        handleFailure();
        return { success: false };
      }

      const matchingOption = ALLOWED_LOCALIDADES.find(option => option.toLowerCase() === trimmed.toLowerCase());
      if (!matchingOption) {
        alert('Ingresa "local" o "foraneo".');
        setNodeValue(previousValue);
        notifyApplied(previousValue, { persisted: false, reverted: true });
        handleFailure();
        return { success: false };
      }

      if (previousValue.trim().toLowerCase() === matchingOption.toLowerCase()) {
        cancelLivePersist(updateKey);
        if (currentGridValue !== matchingOption) {
          applyGridValue(matchingOption);
        } else {
          notifyApplied(matchingOption, { persisted: true });
        }
        recordSuccess(matchingOption);
        return { success: true, appliedValue: matchingOption };
      }

      const persistRequest = async () => {
        const response = await fetch(`${API_BASE_URL}/api/basedatos/actualizar-estatus`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: numericId, field, value: matchingOption })
        });
        const payload = await response.json();
        if (!response.ok || !payload?.ok) {
          throw new Error(payload?.mensaje || 'No se pudo actualizar la localidad.');
        }
        markSelfUpdate();
      };

      const fallbackValue = previousValue;
      setNodeValue(matchingOption);

      if (liveUpdate) {
        scheduleLivePersist(updateKey, async () => {
          try {
            await persistRequest();
            notifyApplied(matchingOption, { persisted: true });
            recordSuccess(matchingOption);
          } catch (err) {
            console.error('No se pudo actualizar la localidad:', err);
            alert('No se pudo guardar la localidad.');
            setNodeValue(fallbackValue);
            notifyApplied(fallbackValue, { persisted: false, reverted: true });
            handleFailure();
          }
        });
        return { success: true, appliedValue: matchingOption, deferred: true };
      }

      try {
        cancelLivePersist(updateKey);
        await persistRequest();
        notifyApplied(matchingOption, { persisted: true });
        recordSuccess(matchingOption);
        return { success: true, appliedValue: matchingOption };
      } catch (err) {
        console.error('No se pudo actualizar la localidad:', err);
        alert('No se pudo guardar la localidad.');
        setNodeValue(fallbackValue);
        notifyApplied(fallbackValue, { persisted: false, reverted: true });
        handleFailure();
        return { success: false };
      }
    }

    if (esCampoEstatus || esEstatusPortalQ || (esFechaCancelacion && puedeEditarPortalQFila)) {
      const finalValue = incomingValue.trim();
      let optimisticDiasSinRecuperar = null;
      if (field === 'DIAS_ENTREGADO1') {
        const normalizedStatus = normalizeCancelacionStatus(finalValue);
        if (normalizedStatus === 'RECUPERAR EFECTIVO') {
          optimisticDiasSinRecuperar = '0 dias';
        } else if (ESTATUS_CANCELACION_PAUSA.has(normalizedStatus)) {
          optimisticDiasSinRecuperar = '0 dias';
        } else if (isRecoveringCancelacionStatus(normalizedStatus)) {
          optimisticDiasSinRecuperar = buildDiasSinRecuperarLabel(data?.BACK_ORDER, data?.FECHA_CANCELACION_DE_PIEZA, new Date());
        } else {
          optimisticDiasSinRecuperar = '';
        }
      }

      const syncStatusField = field === 'ESTATUS2'
        ? 'ESTATUS_DE_PAGO'
        : (field === 'ESTATUS_DE_PAGO' ? 'ESTATUS2' : '');
      const syncStatusUpdate = syncStatusField
        ? getFacturacionSyncStatusUpdate(finalValue)
        : { shouldSync: false, value: '' };
      const syncStatusValue = syncStatusUpdate.value;
      const currentSyncStatusValue = syncStatusField
        ? (data?.[syncStatusField] == null ? '' : String(data[syncStatusField]))
        : '';
      const shouldSyncFacturacionStatus = Boolean(
        syncStatusField
        && syncStatusUpdate.shouldSync
        && normalizeFacturacionSyncStatus(currentSyncStatusValue) !== normalizeFacturacionSyncStatus(syncStatusValue)
      );
      const previousSyncStatusValue = shouldSyncFacturacionStatus ? currentSyncStatusValue : '';

      if (previousValue === finalValue && !shouldSyncFacturacionStatus) {
        cancelLivePersist(updateKey);
        if (currentGridValue !== finalValue) {
          applyGridValue(finalValue);
        } else {
          notifyApplied(finalValue, { persisted: true });
        }
        recordSuccess(finalValue);
        return { success: true, appliedValue: finalValue };
      }

      const persistRequest = async () => {
        markSelfUpdate();
        let response, payload;
        const applyPayloadExtraChanges = (payloadData) => {
          if (!Array.isArray(payloadData?.extraChanges)) return;
          payloadData.extraChanges.forEach((change) => {
            const extraField = change?.field;
            if (!extraField) return;
            const nextValue = change?.value == null ? '' : String(change.value);
            const currentValue = node?.data?.[extraField] == null ? '' : String(node.data[extraField]);
            if (currentValue === nextValue) return;
            if (node?.setDataValue) {
              const suppressionKey = getCellChangeSuppressionKey(node, extraField, node?.data);
              if (suppressionKey) {
                suppressedCellValueChangesRef.current.add(suppressionKey);
                setTimeout(() => {
                  suppressedCellValueChangesRef.current.delete(suppressionKey);
                }, 800);
              }
              try {
                revertingRef.current = true;
                node.setDataValue(extraField, nextValue);
              } finally {
                revertingRef.current = false;
              }
            }
            updateBaseRowValue(numericId, extraField, nextValue, rowKey);
          });
        };

        if (field === 'DIAS_ENTREGADO1' && optimisticDiasSinRecuperar != null) {
          response = await fetch(`${API_BASE_URL}/api/basedatos/actualizar-estatus`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: numericId,
              field,
              value: finalValue,
              extraField: 'DIAS_ENTREGADO2',
              extraValue: optimisticDiasSinRecuperar
            })
          });
          payload = await response.json();
          if (!response.ok || !payload?.ok) {
            throw new Error(payload?.mensaje || 'No se pudo actualizar el valor.');
          }
          updateBaseRowValue(numericId, 'DIAS_ENTREGADO2', optimisticDiasSinRecuperar, rowKey);
          applyPayloadExtraChanges(payload);
        } else {
          response = await fetch(`${API_BASE_URL}/api/basedatos/actualizar-estatus`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: numericId, field, value: finalValue, usuario: usuarioNombre })
          });
          payload = await response.json();
          if (!response.ok || !payload?.ok) {
            throw new Error(payload?.mensaje || 'No se pudo actualizar el valor.');
          }
          applyPayloadExtraChanges(payload);
        }
      };

      const fallbackValue = previousValue;
      setNodeValue(finalValue);
      if (field === 'DIAS_ENTREGADO1' && optimisticDiasSinRecuperar != null) {
        if (node?.setDataValue) {
          node.setDataValue('DIAS_ENTREGADO2', optimisticDiasSinRecuperar);
        }
        updateBaseRowValue(numericId, 'DIAS_ENTREGADO2', optimisticDiasSinRecuperar, rowKey);
      }
      if (shouldSyncFacturacionStatus) {
        if (node?.setDataValue) {
          const suppressionKey = getCellChangeSuppressionKey(node, syncStatusField, node?.data);
          if (suppressionKey) {
            suppressedCellValueChangesRef.current.add(suppressionKey);
            setTimeout(() => {
              suppressedCellValueChangesRef.current.delete(suppressionKey);
            }, 800);
          }
          try {
            revertingRef.current = true;
            node.setDataValue(syncStatusField, syncStatusValue);
          } finally {
            revertingRef.current = false;
          }
        }
        updateBaseRowValue(numericId, syncStatusField, syncStatusValue, rowKey);
      }

      if (liveUpdate) {
        scheduleLivePersist(updateKey, async () => {
          try {
            await persistRequest();
            notifyApplied(finalValue, { persisted: true });
            recordSuccess(finalValue);
          } catch (err) {
            console.error('No se pudo actualizar el valor:', err);
            alert('No se pudo guardar el cambio.');
            setNodeValue(fallbackValue);
            if (shouldSyncFacturacionStatus) {
              if (node?.setDataValue) {
                const suppressionKey = getCellChangeSuppressionKey(node, syncStatusField, node?.data);
                if (suppressionKey) {
                  suppressedCellValueChangesRef.current.add(suppressionKey);
                  setTimeout(() => {
                    suppressedCellValueChangesRef.current.delete(suppressionKey);
                  }, 800);
                }
                try {
                  revertingRef.current = true;
                  node.setDataValue(syncStatusField, previousSyncStatusValue);
                } finally {
                  revertingRef.current = false;
                }
              }
              updateBaseRowValue(numericId, syncStatusField, previousSyncStatusValue, rowKey);
            }
            notifyApplied(fallbackValue, { persisted: false, reverted: true });
            handleFailure();
          }
        });
        return { success: true, appliedValue: finalValue, deferred: true };
      }

      try {
        cancelLivePersist(updateKey);
        await persistRequest();
        notifyApplied(finalValue, { persisted: true });
        recordSuccess(finalValue);
        return { success: true, appliedValue: finalValue };
      } catch (err) {
        console.error('No se pudo actualizar el valor:', err);
        alert('No se pudo guardar el cambio.');
        setNodeValue(fallbackValue);
        if (shouldSyncFacturacionStatus) {
          if (node?.setDataValue) {
            const suppressionKey = getCellChangeSuppressionKey(node, syncStatusField, node?.data);
            if (suppressionKey) {
              suppressedCellValueChangesRef.current.add(suppressionKey);
              setTimeout(() => {
                suppressedCellValueChangesRef.current.delete(suppressionKey);
              }, 800);
            }
            try {
              revertingRef.current = true;
              node.setDataValue(syncStatusField, previousSyncStatusValue);
            } finally {
              revertingRef.current = false;
            }
          }
          updateBaseRowValue(numericId, syncStatusField, previousSyncStatusValue, rowKey);
        }
        notifyApplied(fallbackValue, { persisted: false, reverted: true });
        handleFailure();
        return { success: false };
      }
    }

    if (requiereSupervisor || puedeEditarPedido || puedeEditarOC || puedeEditarPrecioFila) {
  const trimmedIncoming = incomingValue.trim();
  const finalValue = field === 'COMPAQ' ? trimmedIncoming.toUpperCase() : trimmedIncoming;
  const previousComparable = field === 'COMPAQ' ? previousValue.trim().toUpperCase() : previousValue;

      if (field === 'COMPAQ') {
        if (!finalValue) {
          setNodeValue(previousValue);
          notifyApplied(previousValue, { persisted: false, reverted: true });
          handleFailure();
          return { success: false };
        }
        if (!COMPAQ_OPTIONS.includes(finalValue)) {
          alert('Selecciona "GENERADO" o "GENERAR".');
          setNodeValue(previousValue);
          notifyApplied(previousValue, { persisted: false, reverted: true });
          handleFailure();
          return { success: false };
        }
      }

      if (previousComparable === finalValue) {
        cancelLivePersist(updateKey);
        if (currentGridValue !== finalValue) {
          applyGridValue(finalValue);
        } else {
          notifyApplied(finalValue, { persisted: true });
        }
        recordSuccess(finalValue);
        return { success: true, appliedValue: finalValue };
      }

      const persistRequest = async () => {
        const response = await fetch(`${API_BASE_URL}/api/basedatos/captura/actualizar-celda`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: numericId, field, value: finalValue, usuario: usuarioNombre })
        });
        const payload = await response.json();
        if (!response.ok || !payload?.ok) {
          throw new Error(payload?.mensaje || 'No se pudo actualizar el valor.');
        }

        if (Array.isArray(payload?.extraChanges)) {
          payload.extraChanges.forEach((change) => {
            const extraField = change?.field;
            if (!extraField) return;
            const nextValue = change?.value == null ? '' : String(change.value);
            const currentValue = node?.data?.[extraField] == null ? '' : String(node.data[extraField]);
            if (currentValue === nextValue) return;
            if (node?.setDataValue) {
              const suppressionKey = getCellChangeSuppressionKey(node, extraField, node?.data);
              if (suppressionKey) {
                suppressedCellValueChangesRef.current.add(suppressionKey);
                setTimeout(() => {
                  suppressedCellValueChangesRef.current.delete(suppressionKey);
                }, 800);
              }
              try {
                revertingRef.current = true;
                node.setDataValue(extraField, nextValue);
              } finally {
                revertingRef.current = false;
              }
            }
            updateBaseRowValue(numericId, extraField, nextValue, rowKey);
          });
        }

        if ((field === 'NUMERO DE FACTURA' || field === 'UUID' || field === 'ESTATUS2' || field === 'ESTATUS_DE_PAGO') && Array.isArray(payload?.updatedRowIds) && payload.updatedRowIds.length) {
          const updatedIdSet = new Set(
            payload.updatedRowIds
              .map((item) => Number(item))
              .filter((item) => Number.isInteger(item))
          );
          if (updatedIdSet.size) {
            const isNumeroFacturaField = field === 'NUMERO DE FACTURA';
            const isUuidField = field === 'UUID';
            const groupedStatusValue = (
              (field === 'ESTATUS2' || field === 'ESTATUS_DE_PAGO')
                ? (normalizeFacturacionSyncStatus(finalValue) === 'pagado'
                    ? 'PAGADO'
                    : (normalizeFacturacionSyncStatus(finalValue) === 'facturar' ? 'FACTURAR' : ''))
                : ''
            );
            const isGroupedStatusField = groupedStatusValue !== '';
            setBaseDataRaw((prev) => {
              if (!Array.isArray(prev) || !prev.length) return prev;
              let changed = false;
              const nextRows = prev.map((row) => {
                const rowId = Number(row?.id);
                if (!Number.isInteger(rowId) || !updatedIdSet.has(rowId)) return row;
                const nextFactura = isNumeroFacturaField ? finalValue : (row?.['NUMERO DE FACTURA'] == null ? '' : String(row['NUMERO DE FACTURA']));
                const nextUuid = isUuidField ? finalValue : (row?.UUID == null ? '' : String(row.UUID));
                const nextEstatus2 = isNumeroFacturaField
                  ? 'FACTURADO'
                  : (isGroupedStatusField ? groupedStatusValue : (row?.ESTATUS2 == null ? '' : String(row.ESTATUS2)));
                const nextEstatusPago = isNumeroFacturaField
                  ? 'FACTURADO'
                  : (isGroupedStatusField ? groupedStatusValue : (row?.ESTATUS_DE_PAGO == null ? '' : String(row.ESTATUS_DE_PAGO)));
                const currentFactura = row?.['NUMERO DE FACTURA'] == null ? '' : String(row['NUMERO DE FACTURA']);
                const currentUuid = row?.UUID == null ? '' : String(row.UUID);
                const currentEstatus2 = row?.ESTATUS2 == null ? '' : String(row.ESTATUS2);
                const currentEstatusPago = row?.ESTATUS_DE_PAGO == null ? '' : String(row.ESTATUS_DE_PAGO);
                if (
                  currentFactura === nextFactura
                  && currentUuid === nextUuid
                  && currentEstatus2 === nextEstatus2
                  && currentEstatusPago === nextEstatusPago
                ) {
                  return row;
                }
                changed = true;
                return {
                  ...row,
                  'NUMERO DE FACTURA': nextFactura,
                  UUID: nextUuid,
                  ESTATUS2: nextEstatus2,
                  ESTATUS_DE_PAGO: nextEstatusPago
                };
              });
              return changed ? nextRows : prev;
            });
          }
        }

        markSelfUpdate();
      };

      const fallbackValue = previousValue;
      setNodeValue(finalValue);

      if (liveUpdate) {
        scheduleLivePersist(updateKey, async () => {
          try {
            await persistRequest();
            notifyApplied(finalValue, { persisted: true });
            recordSuccess(finalValue);
          } catch (err) {
            console.error('No se pudo actualizar el valor:', err);
            alert('No se pudo guardar el cambio.');
            setNodeValue(fallbackValue);
            notifyApplied(fallbackValue, { persisted: false, reverted: true });
            handleFailure();
          }
        });
        return { success: true, appliedValue: finalValue, deferred: true };
      }

      try {
        cancelLivePersist(updateKey);
        await persistRequest();
        notifyApplied(finalValue, { persisted: true });
        recordSuccess(finalValue);
        return { success: true, appliedValue: finalValue };
      } catch (err) {
        console.error('No se pudo actualizar el valor:', err);
        alert('No se pudo guardar el cambio.');
        setNodeValue(fallbackValue);
        notifyApplied(fallbackValue, { persisted: false, reverted: true });
        handleFailure();
        return { success: false };
      }
    }

    handleFailure();
    return { success: false };
  }, [canEditBackOrderQualitasForRow, canEditPedidoForRow, esCaptura, esSupervisor, puedeEditarPortalQ, puedeEditarPrecioMultiasistencia, puedeGestionarNuevoEstatus, scheduleLivePersist, cancelLivePersist, getCellChangeSuppressionKey, markSelfUpdate, updateBaseRowValue, pushUndoEntry, restoreUndoEntry, usuarioNombre]);

  const handlePrincipalCellEdit = useCallback((params) => {
    if (revertingRef.current) return;

    const field = params?.colDef?.field;
    if (!field) return;

    const suppressionKey = getCellChangeSuppressionKey(params?.node, field, params?.data);
    if (suppressionKey && suppressedCellValueChangesRef.current.has(suppressionKey)) {
      suppressedCellValueChangesRef.current.delete(suppressionKey);
      return;
    }

    processEditableCellChange({
      field,
      newValue: params?.newValue,
      oldValue: params?.oldValue,
      node: params?.node,
      data: params?.data,
      onApplied: (appliedValue) => {
        const normalized = appliedValue == null ? '' : String(appliedValue);
        setCellEditorDialog(prev => {
          if (!prev) return prev;
          if (prev.rowNode !== params?.node || prev.field !== field) return prev;
          return {
            ...prev,
            data: params?.node?.data || prev.data,
            value: normalized,
            originalValue: normalized,
            isDirty: false,
            isCommitting: false
          };
        });
      }
    }).catch(err => {
      console.error('Error al procesar el cambio:', err);
    });
  }, [getCellChangeSuppressionKey, processEditableCellChange]);

  const handleUndo = useCallback(() => {
    const stack = undoStackRef.current;
    if (!stack.length) return;
    if (suppressHistoryRef.current || pendingUndoRef.current) return;

    const entry = stack.pop();
    setCanUndo(stack.length > 0);

    if (!entry) return;

    const api = gridRef.current?.api;
    if (!api) {
      stack.push(entry);
      if (stack.length > 5) {
        stack.shift();
      }
      setCanUndo(stack.length > 0);
      return;
    }

    let targetNode = null;
    if (entry.rowNodeId != null) {
      targetNode = api.getRowNode(entry.rowNodeId);
    }

    if (!targetNode) {
      api.forEachNode(node => {
        if (targetNode) return;
        const dataId = node.data?.id;
        if (dataId != null && entry.rowId != null && String(dataId) === String(entry.rowId)) {
          targetNode = node;
          return;
        }
        if (entry.rowKey) {
          const key = buildPedidoItemKey(node.data?.PEDIDO, node.data?.ITEM);
          if (key && key === entry.rowKey) {
            targetNode = node;
          }
        }
      });
    }

    if (!targetNode) {
      stack.push(entry);
      if (stack.length > 5) {
        stack.shift();
      }
      setCanUndo(stack.length > 0);
      alert('No se encontró el renglón para deshacer el cambio.');
      return;
    }

    pendingUndoRef.current = entry;
    suppressHistoryRef.current = true;
    targetNode.setDataValue(entry.field, entry.previousValue ?? '');
    setCanUndo(stack.length > 0);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      if (event.key !== 'z' && event.key !== 'Z') return;
      const tagName = (event.target?.tagName || '').toLowerCase();
      const isEditableField = tagName === 'input' || tagName === 'textarea' || event.target?.isContentEditable;
      if (isEditableField) return;
      event.preventDefault();
      if (canUndo) {
        handleUndo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, canUndo]);

  const getExportableColumnDescriptors = useCallback((columnApi) => {
    const flattenDefs = (defs) => {
      const collected = [];
      defs?.forEach(def => {
        if (!def) return;
        if (Array.isArray(def.children) && def.children.length) {
          collected.push(...flattenDefs(def.children));
        } else {
          collected.push(def);
        }
      });
      return collected;
    };

    const columnInstances = columnApi?.getAllColumns?.() || [];
    const columnDescriptors = columnInstances.length
      ? columnInstances.map(column => ({
          column,
          colDef: column.getColDef?.() || {}
        }))
      : flattenDefs(columnDefs).map(colDef => ({ column: null, colDef }));

    return columnDescriptors.filter(({ colDef }) => {
      if (!colDef) return false;
      if (colDef.suppressExport) return false;
      if (colDef.field === 'checked') return false;
      if (colDef.checkboxSelection || colDef.headerCheckboxSelection) return false;
      if (!colDef.field && typeof colDef.valueGetter !== 'function') return false;
      return true;
    });
  }, [columnDefs]);

  const serializeNodeForExport = (node, descriptors, api, columnApi) =>
    descriptors.map(({ column, colDef }) => {
      let rawValue;

      if (typeof colDef.valueGetter === 'function') {
        try {
          rawValue = colDef.valueGetter({
            api,
            columnApi,
            context: api?.context,
            data: node.data,
            node,
            colDef,
            column,
            getValue: (field) => (node?.data ? node.data[field] : undefined)
          });
        } catch (err) {
          console.error('No se pudo obtener el valor calculado:', err);
          rawValue = null;
        }
      } else if (colDef.field) {
        rawValue = node?.data ? node.data[colDef.field] : undefined;
      } else if (column && typeof column.getColId === 'function') {
        rawValue = node?.data ? node.data[column.getColId()] : undefined;
      } else if (colDef.colId) {
        rawValue = node?.data ? node.data[colDef.colId] : undefined;
      } else {
        rawValue = undefined;
      }

      if (rawValue === null || typeof rawValue === 'undefined') return '';
      if (rawValue instanceof Date) return rawValue.toISOString();
      if (typeof rawValue === 'object') return JSON.stringify(rawValue);
      return rawValue;
    });

  const buildExportHeaderRow = (descriptors, columnApi) =>
    descriptors.map(({ column, colDef }, index) => {
      const displayName = column && columnApi?.getDisplayNameForColumn?.(column, 'header');
      if (displayName) return displayName;
      if (colDef.headerName) return colDef.headerName;
      if (colDef.field) return colDef.field;
      if (colDef.colId) return colDef.colId;
      if (column && typeof column.getColId === 'function') return column.getColId();
      return `Columna ${index + 1}`;
    });

  const exportRowsToExcel = (headerRow, rows, sheetName, filePrefix) => {
    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const fileName = `${filePrefix}-${timestamp}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const handleExportPrincipalToExcel = useCallback(() => {
    const api = gridRef.current?.api;
    const columnApi = gridRef.current?.columnApi;
    if (!api) {
      alert('No se pudo acceder a la tabla para exportar.');
      return;
    }

    const exportableDescriptors = getExportableColumnDescriptors(columnApi);
    if (!exportableDescriptors.length) {
      alert('No hay columnas disponibles para exportar.');
      return;
    }

    const rows = [];
    api.forEachNodeAfterFilterAndSort(node => {
      rows.push(serializeNodeForExport(node, exportableDescriptors, api, columnApi));
    });

    if (!rows.length) {
      alert('No hay registros para exportar.');
      return;
    }

    const headerRow = buildExportHeaderRow(exportableDescriptors, columnApi);
    exportRowsToExcel(headerRow, rows, 'Base de Datos', 'base-datos');
  }, [getExportableColumnDescriptors]);

  const handleExportNuevoEstatusSelection = useCallback(() => {
    const api = nuevoEstatusGridRef.current?.api;
    const columnApi = nuevoEstatusGridRef.current?.columnApi;
    if (!api) {
      alert('No se pudo acceder a la tabla de nuevo estatus.');
      return;
    }

    const selectedNodes = typeof api.getSelectedNodes === 'function' ? api.getSelectedNodes() : [];
    if (!selectedNodes.length) {
      alert('Selecciona al menos una fila para exportar.');
      return;
    }

    const exportableDescriptors = getExportableColumnDescriptors(columnApi);
    if (!exportableDescriptors.length) {
      alert('No hay columnas disponibles para exportar.');
      return;
    }

    const rows = selectedNodes.map(node => serializeNodeForExport(node, exportableDescriptors, api, columnApi));
    const headerRow = buildExportHeaderRow(exportableDescriptors, columnApi);
    exportRowsToExcel(headerRow, rows, 'Nuevo estatus', 'nuevo-estatus');
  }, [getExportableColumnDescriptors]);

  const updateInspectorFromParams = useCallback((params) => {
    const field = params?.colDef?.field;
    if (!field) {
      setCellEditorDialog(null);
      return;
    }
    const node = params?.node;
    if (!node) {
      setCellEditorDialog(null);
      return;
    }

    const rowData = params?.data || node.data || {};
    const rawValue = rowData[field];
    const normalizedValue = rawValue == null ? '' : String(rawValue);
    let inspectorValue = normalizedValue;
    if (field === 'LOCALIDAD') {
      const matchingOption = ALLOWED_LOCALIDADES.find(option => option.toLowerCase() === normalizedValue.toLowerCase());
      inspectorValue = matchingOption || normalizedValue.toLowerCase();
    } else if (field === 'COMPAQ') {
      const matchingCompaq = COMPAQ_OPTIONS.find(option => option.toLowerCase() === normalizedValue.toLowerCase());
      inspectorValue = matchingCompaq || '';
    } else if (field === 'DIAS_ENTREGADO1') {
      const matchingStatus = ESTATUS_CANCELACION_OPTIONS.find(option => option.toLowerCase() === normalizedValue.toLowerCase());
      inspectorValue = matchingStatus || '';
    }

    inspectorCommitRef.current = false;
    setCellEditorDialog({
      field,
      header: columnLabels[field] || field,
      rowNode: node,
      data: rowData,
      originalValue: normalizedValue,
      value: inspectorValue,
      isDirty: false,
      isCommitting: false
    });
  }, [columnLabels]);

  const openRowQuickView = useCallback((params) => {
    if (params?.colDef?.field === 'DIAS_ENTREGADO1') return;
    const rowData = params?.data || params?.node?.data;
    if (!rowData) return;
    const rawRegistroId = rowData.id ?? rowData.ID;
    const parsedRegistroId = Number(rawRegistroId);
    const registroId = Number.isInteger(parsedRegistroId) && parsedRegistroId > 0 ? parsedRegistroId : null;

    const displayedColumns = params?.api?.getAllDisplayedColumns?.() || [];
    const visibleFields = displayedColumns
      .map((column) => {
        const colDef = column?.getColDef?.() || {};
        const field = colDef.field;
        if (!field) return null;
        if (field === 'checked') return null;
        if (typeof field === 'string' && field.startsWith('__')) return null;
        return {
          field,
          label: colDef.headerName || columnLabels[field] || field,
          value: rowData[field] == null ? '' : String(rowData[field]),
          editable: canEditField(field, rowData)
        };
      })
      .filter(Boolean);

    const pedido = pickRowValue(rowData, ['PEDIDO', 'pedido']);

    setRowQuickView({
      title: 'Detalle de la fila',
      subtitle: pedido ? `Pedido ${pedido}` : 'Sin pedido identificado',
      fields: visibleFields,
      rowNode: params?.node || null,
      registroId
    });
    setRowQuickViewEditError('');
  }, [canEditField, columnLabels]);

  const consumeTripleClick = useCallback((stateRef, params) => {
    const rowData = params?.data || params?.node?.data;
    const field = params?.colDef?.field;
    if (!rowData || !field) return false;
    if (field === 'checked') return false;
    if (typeof field === 'string' && field.startsWith('__')) return false;

    const rowId = rowData.id ?? rowData.ID ?? params?.node?.id ?? '';
    const clickKey = `${String(rowId)}|||${field}`;
    const now = Date.now();
    const tracker = stateRef.current;

    if (tracker.key === clickKey && (now - tracker.lastAt) <= QUICK_VIEW_TRIPLE_CLICK_WINDOW_MS) {
      tracker.count += 1;
    } else {
      tracker.key = clickKey;
      tracker.count = 1;
    }
    tracker.lastAt = now;

    if (tracker.count >= 3) {
      tracker.key = '';
      tracker.count = 0;
      tracker.lastAt = 0;
      return true;
    }
    return false;
  }, []);

  const handleCellClicked = useCallback((params) => {
    updateInspectorFromParams(params);
    if (consumeTripleClick(principalQuickViewClickRef, params)) {
      openRowQuickView(params);
    }
  }, [consumeTripleClick, openRowQuickView, updateInspectorFromParams]);

  const handleNuevoEstatusCellClicked = useCallback((params) => {
    if (consumeTripleClick(nuevoEstatusQuickViewClickRef, params)) {
      openRowQuickView(params);
    }
  }, [consumeTripleClick, openRowQuickView]);

  const handleCloseRowQuickView = useCallback(() => {
    setRowQuickView(null);
    setRowQuickViewAdjuntosLoading(false);
    setRowQuickViewAdjuntosCount(null);
    setRowQuickViewAdjuntosError('');
    setRowQuickViewSavingField('');
    setRowQuickViewEditError('');
  }, []);

  const handleRowQuickViewFieldChange = useCallback((field, nextValue) => {
    setRowQuickView((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        fields: prev.fields.map((entry) => (
          entry.field === field
            ? { ...entry, value: nextValue }
            : entry
        ))
      };
    });
  }, []);

  const handleRowQuickViewFieldCommit = useCallback(async (field) => {
    if (!rowQuickView?.rowNode || !field) return;
    const targetField = rowQuickView.fields.find((entry) => entry.field === field);
    if (!targetField || !targetField.editable) return;

    const rowNode = rowQuickView.rowNode;
    const rowData = rowNode.data || {};
    setRowQuickViewSavingField(field);
    setRowQuickViewEditError('');

    try {
      await processEditableCellChange({
        field,
        newValue: targetField.value,
        oldValue: rowData[field],
        node: rowNode,
        data: rowData,
        onApplied: (appliedValue) => {
          const normalized = appliedValue == null ? '' : String(appliedValue);
          setRowQuickView((prev) => {
            if (!prev || prev.rowNode !== rowNode) return prev;
            return {
              ...prev,
              fields: prev.fields.map((entry) => (
                entry.field === field
                  ? { ...entry, value: normalized }
                  : entry
              ))
            };
          });
        }
      });
    } catch (error) {
      console.error('No se pudo guardar desde el modal de fila:', error);
      setRowQuickViewEditError(error?.message || 'No se pudo guardar el cambio.');
    } finally {
      setRowQuickViewSavingField('');
    }
  }, [rowQuickView, processEditableCellChange]);

  const handleCellFocused = useCallback((params) => {
    if (!params?.column) return;
    const field = params.column.getColDef()?.field;
    if (!field) {
      setCellEditorDialog(null);
      return;
    }
    const rowIndex = params.rowIndex;
    if (typeof rowIndex !== 'number' || rowIndex < 0) {
      setCellEditorDialog(null);
      return;
    }
    const node = params.api?.getDisplayedRowAtIndex(rowIndex);
    if (!node) {
      setCellEditorDialog(null);
      return;
    }
    updateInspectorFromParams({
      colDef: params.column.getColDef(),
      node,
      data: node.data,
      value: node.data?.[field]
    });
  }, [updateInspectorFromParams]);

  const commitInspectorDialog = useCallback((dialog) => {
    if (!dialog || !dialog.rowNode || !dialog.field) return;
    if (!dialog.isDirty || dialog.isCommitting || inspectorCommitRef.current) return;

    const { rowNode, field, value } = dialog;
    const rowData = rowNode.data || {};
    const previousValue = rowData[field];

    inspectorCommitRef.current = true;

    setCellEditorDialog(current => {
      if (!current || current.rowNode !== rowNode || current.field !== field) return current;
      return { ...current, isCommitting: true };
    });

    processEditableCellChange({
      field,
      newValue: value,
      oldValue: previousValue,
      node: rowNode,
      data: rowData,
      onApplied: (appliedValue) => {
        const normalizedApplied = appliedValue == null ? '' : String(appliedValue);
        setCellEditorDialog(current => {
          if (!current || current.rowNode !== rowNode || current.field !== field) return current;
          return {
            ...current,
            data: rowNode.data || current.data,
            value: normalizedApplied,
            originalValue: normalizedApplied,
            isDirty: false,
            isCommitting: false
          };
        });
      }
    })
      .then((result) => {
        if (result?.success === false) {
          setCellEditorDialog(current => {
            if (!current || current.rowNode !== rowNode || current.field !== field) return current;
            const refreshedData = rowNode.data || {};
            const refreshedValue = refreshedData[field] == null ? '' : String(refreshedData[field]);
            return {
              ...current,
              data: refreshedData,
              value: refreshedValue,
              originalValue: refreshedValue,
              isDirty: false,
              isCommitting: false
            };
          });
        }
      })
      .catch((err) => {
        console.error('No se pudo aplicar el cambio desde el inspector:', err);
        setCellEditorDialog(current => {
          if (!current || current.rowNode !== rowNode || current.field !== field) return current;
          const refreshedData = rowNode.data || {};
          const refreshedValue = refreshedData[field] == null ? '' : String(refreshedData[field]);
          return {
            ...current,
            data: refreshedData,
            value: refreshedValue,
            originalValue: refreshedValue,
            isDirty: false,
            isCommitting: false
          };
        });
      })
      .finally(() => {
        inspectorCommitRef.current = false;
        setCellEditorDialog(current => {
          if (!current || current.rowNode !== rowNode || current.field !== field) return current;
          if (!current.isCommitting) return current;
          return { ...current, isCommitting: false };
        });
      });
  }, [processEditableCellChange, inspectorCommitRef]);

  const handleInspectorChange = useCallback((field, nextValue) => {
    setCellEditorDialog(prev => {
      if (!prev || prev.field !== field) return prev;
      if (prev.value === nextValue) return prev;
      return {
        ...prev,
        value: nextValue,
        isDirty: true
      };
    });
  }, []);

  const handleDialogValueChange = useCallback((event) => {
    if (!cellEditorDialog) return;
    const field = cellEditorDialog.field;
    const rowData = cellEditorDialog.data || cellEditorDialog.rowNode?.data || null;
    if (!canEditField(field, rowData)) return;
    let nextValue = event?.target?.value ?? '';
    if (field === 'COMPAQ') {
      nextValue = nextValue.toString().trim().toUpperCase();
    } else if (field === 'DIAS_ENTREGADO1') {
      nextValue = nextValue.toString().trim().toUpperCase();
      const matchingStatus = ESTATUS_CANCELACION_OPTIONS.find(option => option.toLowerCase() === nextValue.toLowerCase());
      nextValue = matchingStatus || '';
    }

    handleInspectorChange(field, nextValue);

    if (field === 'LOCALIDAD' || field === 'COMPAQ' || field === 'DIAS_ENTREGADO1') {
      commitInspectorDialog({
        ...cellEditorDialog,
        value: nextValue,
        isDirty: true,
        isCommitting: false
      });
    }
  }, [cellEditorDialog, handleInspectorChange, canEditField, commitInspectorDialog]);

  const handleDialogBlur = useCallback(() => {
    if (inspectorSuppressCommitRef.current) {
      inspectorSuppressCommitRef.current = false;
      return;
    }
    if (!cellEditorDialog) return;
    const rowData = cellEditorDialog.data || cellEditorDialog.rowNode?.data || null;
    if (!canEditField(cellEditorDialog.field, rowData)) return;
    commitInspectorDialog(cellEditorDialog);
  }, [cellEditorDialog, canEditField, commitInspectorDialog, inspectorSuppressCommitRef]);

  const handleDialogCancel = useCallback(() => {
    inspectorSuppressCommitRef.current = true;
    setTimeout(() => {
      inspectorSuppressCommitRef.current = false;
    }, 0);
    if (cellEditorDialog?.rowNode && cellEditorDialog.field) {
      const rowData = cellEditorDialog.rowNode.data || {};
      const numericId = Number(rowData?.id);
      if (Number.isInteger(numericId)) {
        const key = `${numericId}:${cellEditorDialog.field}`;
        Promise.resolve(flushLivePersist(key)).catch(err => {
          console.error('No se pudo completar el guardado al cerrar el cuadro:', err);
        });
      }
    }
    gridRef.current?.api?.clearFocusedCell();
    inspectorCommitRef.current = false;
    setCellEditorDialog(null);
  }, [cellEditorDialog, flushLivePersist, inspectorCommitRef, inspectorSuppressCommitRef]);

  const inspectorState = cellEditorDialog;
  const inspectorField = inspectorState?.field ?? null;
  const inspectorHeader = inspectorState?.header ?? '';
  const inspectorValue = inspectorState?.value ?? '';
  const inspectorRowData = inspectorState?.data || null;
  const inspectorIsEditable = inspectorField
    ? canEditField(inspectorField, inspectorRowData) && !inspectorState?.isCommitting
    : false;
  const inspectorPedidoLabel = inspectorRowData?.PEDIDO ?? inspectorRowData?.pedido ?? '';
  const inspectorSiniestroLabel = inspectorRowData?.SINIESTRO ?? inspectorRowData?.siniestro ?? '';
  const inspectorItemLabel = inspectorRowData?.ITEM ?? inspectorRowData?.item ?? '';
  const inspectorModeloLabel = inspectorRowData?.MODELO ?? inspectorRowData?.modelo ?? '';
  const inspectorAnioLabel = inspectorRowData?.ANIO ?? inspectorRowData?.anio ?? '';
  const inspectorModeloAnioLabel = [inspectorModeloLabel, inspectorAnioLabel]
    .map((value) => (value == null ? '' : String(value).trim()))
    .filter(Boolean)
    .join(' / ');
  const inspectorTallerLabel = inspectorRowData?.NOMBRE_COMERCIAL_TALLER
    ?? inspectorRowData?.nombre_comercial_taller
    ?? '';
  const inspectorOrigenLabel = inspectorRowData?.ORIGEN ?? inspectorRowData?.origen ?? '';
  const selectedRegistroId = useMemo(() => {
    if (!inspectorRowData) return null;
    const rawId = inspectorRowData.id ?? inspectorRowData.ID;
    const parsed = Number(rawId);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [inspectorRowData]);
  const hasRegistroSeleccionado = Boolean(selectedRegistroId);
  const adjuntosSlotsDisponibles = selectedRegistroId
    ? Math.max(ADJUNTOS_MAX_PER_REGISTRO - adjuntos.length, 0)
    : ADJUNTOS_MAX_PER_REGISTRO;

  useEffect(() => {
    if (!cellEditorDialog || !inspectorIsEditable) return;
    const currentField = cellEditorDialog.field;
    const rowNode = cellEditorDialog.rowNode;
    const rowKey = rowNode ? (rowNode.id ?? rowNode?.data?.id ?? null) : null;
    const previous = inspectorFocusTrackerRef.current;
    if (previous.field === currentField && previous.rowKey === rowKey) {
      return;
    }
    inspectorFocusTrackerRef.current = { field: currentField, rowKey };
  }, [cellEditorDialog, inspectorIsEditable]);

  useEffect(() => {
    if (!inspectorIsEditable) {
      inspectorFocusTrackerRef.current = { field: null, rowKey: null };
      dialogInputRef.current = null;
    }
  }, [inspectorIsEditable]);

  useEffect(() => {
    if (!cellEditorDialog) {
      inspectorFocusTrackerRef.current = { field: null, rowKey: null };
    }
  }, [cellEditorDialog]);

  useEffect(() => {
    if (!selectedRegistroId) {
      setAdjuntos([]);
      setAdjuntosError('');
      setAdjuntosLoading(false);
      return;
    }
    const controller = new AbortController();
    setAdjuntosLoading(true);
    setAdjuntosError('');
    fetch(`${API_BASE_URL}/api/basedatos/${selectedRegistroId}/adjuntos`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload?.mensaje || 'No se pudieron cargar los adjuntos.');
        }
        return response.json();
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        setAdjuntos(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error('No se pudieron cargar los adjuntos:', err);
        setAdjuntos([]);
        setAdjuntosError(err.message || 'No se pudieron cargar los adjuntos.');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setAdjuntosLoading(false);
        }
      });
    return () => controller.abort();
  }, [selectedRegistroId, adjuntosReloadKey]);

  useEffect(() => {
    if (!inspectorState?.rowNode || !inspectorField) return;
    if (inspectorState.isDirty) return;
    const latestData = inspectorState.rowNode.data || {};
    const rawValue = latestData[inspectorField];
    const normalizedValue = rawValue == null ? '' : String(rawValue);
    let nextValue = normalizedValue;
    if (inspectorField === 'LOCALIDAD') {
      nextValue = ALLOWED_LOCALIDADES.find(option => option.toLowerCase() === normalizedValue.toLowerCase()) || normalizedValue.toLowerCase();
    } else if (inspectorField === 'COMPAQ') {
      nextValue = COMPAQ_OPTIONS.find(option => option.toLowerCase() === normalizedValue.toLowerCase()) || '';
    } else if (inspectorField === 'DIAS_ENTREGADO1') {
      nextValue = ESTATUS_CANCELACION_OPTIONS.find(option => option.toLowerCase() === normalizedValue.toLowerCase()) || '';
    }
    setCellEditorDialog(prev => {
      if (!prev) return prev;
      if (prev.rowNode !== inspectorState.rowNode || prev.field !== inspectorField) return prev;
      if (prev.value === nextValue) return prev;
      return { ...prev, data: latestData, value: nextValue, originalValue: normalizedValue };
    });
  }, [inspectorState, inspectorField, principalRowData]);

  const flushAllLiveUpdates = useCallback(() => {
    inspectorCommitRef.current = false;
    const timers = liveUpdateTimersRef.current;
    const pending = Array.from(timers.values());
    timers.clear();
    pending.forEach(({ timeoutId, persistFn }) => {
      clearTimeout(timeoutId);
      Promise.resolve(persistFn()).catch(err => {
        console.error('No se pudo completar una actualización pendiente:', err);
      });
    });
  }, [inspectorCommitRef]);

  useEffect(() => {
    return () => {
      flushAllLiveUpdates();
    };
  }, [flushAllLiveUpdates]);

  const handleDialogKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleDialogCancel();
      return;
    }
    if ((event.key === 'Enter' && (event.ctrlKey || event.metaKey))) {
      event.preventDefault();
      const rowData = cellEditorDialog?.data || cellEditorDialog?.rowNode?.data || null;
      if (cellEditorDialog && canEditField(cellEditorDialog.field, rowData)) {
        commitInspectorDialog(cellEditorDialog);
      }
    }
  }, [handleDialogCancel, cellEditorDialog, canEditField, commitInspectorDialog]);

    const handleAdjuntosReload = useCallback(() => {
      if (!selectedRegistroId) return;
      setAdjuntosReloadKey(value => value + 1);
    }, [selectedRegistroId]);

    const handleAdjuntosUploadClick = useCallback(() => {
      if (!selectedRegistroId) {
        alert('Selecciona un registro para adjuntar archivos.');
        return;
      }
      adjuntosFileInputRef.current?.click();
    }, [selectedRegistroId]);

    const handleAdjuntosFileChange = useCallback(async (event) => {
      if (!selectedRegistroId) return;
      const input = event.target;
      const files = Array.from(input?.files || []);
      if (!files.length) return;
      const disponibles = Math.max(ADJUNTOS_MAX_PER_REGISTRO - adjuntos.length, 0);
      if (disponibles <= 0) {
        alert('Este registro ya tiene el máximo de adjuntos permitidos.');
        if (input) input.value = '';
        return;
      }
      if (files.length > disponibles) {
        alert(`Solo puedes subir ${disponibles} archivo(s) más para este registro.`);
        if (input) input.value = '';
        return;
      }
      setAdjuntosUploading(true);
      try {
        const formData = new FormData();
        files.forEach((file) => formData.append('archivos', file));
        if (usuarioNombre) {
          formData.append('subidoPor', usuarioNombre);
        }
        const response = await fetch(`${API_BASE_URL}/api/basedatos/${selectedRegistroId}/adjuntos`, {
          method: 'POST',
          body: formData
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload?.ok === false) {
          throw new Error(payload?.mensaje || 'No se pudieron subir los archivos.');
        }
        const nuevos = Array.isArray(payload?.adjuntos) ? payload.adjuntos : [];
        if (nuevos.length) {
          setAdjuntos(prev => [...nuevos, ...prev]);
        }
      } catch (err) {
        console.error('No se pudieron subir los adjuntos:', err);
        alert(err.message || 'No se pudieron subir los archivos.');
      } finally {
        setAdjuntosUploading(false);
        if (input) {
          input.value = '';
        }
      }
    }, [selectedRegistroId, adjuntos.length, usuarioNombre]);

    const buildAdjuntoViewUrl = useCallback((adjunto) => (
      `${API_BASE_URL}/api/basedatos/adjuntos/${adjunto?.id}/view`
    ), []);

    const handleAdjuntoPreview = useCallback((adjunto) => {
      if (!adjunto) return;
      if (isImageMimeType(adjunto.mime_type) || isPdfAdjunto(adjunto)) {
        setPreviewAdjunto(adjunto);
        setPreviewPdfPages(0);
        setPreviewPdfCurrentPage(1);
        setPreviewPdfLoadError('');
      } else {
        window.open(buildAdjuntoViewUrl(adjunto), '_blank', 'noopener');
      }
    }, [buildAdjuntoViewUrl]);

    const handleClosePreview = useCallback(() => {
      setPreviewAdjunto(null);
      setPreviewPdfPages(0);
      setPreviewPdfCurrentPage(1);
      setPreviewPdfLoadError('');
    }, []);

    const resetPreviewTransform = useCallback(() => {
      setPreviewZoom(1);
      setPreviewOffset({ x: 0, y: 0 });
      previewGestureRef.current.isDragging = false;
      previewGestureRef.current.touchMode = null;
    }, []);

    const applyPreviewZoomAtPoint = useCallback((nextZoomRaw, clientX, clientY) => {
      const viewport = previewViewportRef.current;
      if (!viewport) return;
      const rect = viewport.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const pointX = clientX - rect.left;
      const pointY = clientY - rect.top;

      setPreviewZoom((currentZoom) => {
        const nextZoom = clampPreviewZoom(nextZoomRaw);
        if (nextZoom === currentZoom) return currentZoom;
        const ratio = nextZoom / currentZoom;
        setPreviewOffset((currentOffset) => ({
          x: pointX - centerX - (pointX - centerX - currentOffset.x) * ratio,
          y: pointY - centerY - (pointY - centerY - currentOffset.y) * ratio,
        }));
        return nextZoom;
      });
    }, []);

    const handlePreviewZoomIn = useCallback(() => {
      const viewport = previewViewportRef.current;
      if (!viewport) {
        setPreviewZoom((prev) => clampPreviewZoom(prev + 0.25));
        return;
      }
      const rect = viewport.getBoundingClientRect();
      applyPreviewZoomAtPoint(previewZoom + 0.25, rect.left + rect.width / 2, rect.top + rect.height / 2);
    }, [applyPreviewZoomAtPoint, previewZoom]);

    const handlePreviewZoomOut = useCallback(() => {
      const viewport = previewViewportRef.current;
      if (!viewport) {
        setPreviewZoom((prev) => clampPreviewZoom(prev - 0.25));
        return;
      }
      const rect = viewport.getBoundingClientRect();
      applyPreviewZoomAtPoint(previewZoom - 0.25, rect.left + rect.width / 2, rect.top + rect.height / 2);
    }, [applyPreviewZoomAtPoint, previewZoom]);

    const handlePreviewWheel = useCallback((event) => {
      event.preventDefault();
      const direction = event.deltaY > 0 ? -0.2 : 0.2;
      applyPreviewZoomAtPoint(previewZoom + direction, event.clientX, event.clientY);
    }, [applyPreviewZoomAtPoint, previewZoom]);

    const handlePreviewMouseDown = useCallback((event) => {
      if (previewZoom <= 1) return;
      previewGestureRef.current.isDragging = true;
      previewGestureRef.current.dragStartX = event.clientX;
      previewGestureRef.current.dragStartY = event.clientY;
      previewGestureRef.current.dragStartOffsetX = previewOffset.x;
      previewGestureRef.current.dragStartOffsetY = previewOffset.y;
    }, [previewOffset.x, previewOffset.y, previewZoom]);

    const handlePreviewTouchStart = useCallback((event) => {
      if (event.touches.length === 2) {
        const [t1, t2] = event.touches;
        const dx = t2.clientX - t1.clientX;
        const dy = t2.clientY - t1.clientY;
        previewGestureRef.current.touchMode = 'pinch';
        previewGestureRef.current.pinchStartDistance = Math.hypot(dx, dy);
        previewGestureRef.current.pinchStartZoom = previewZoom;
        previewGestureRef.current.pinchStartOffsetX = previewOffset.x;
        previewGestureRef.current.pinchStartOffsetY = previewOffset.y;
        return;
      }

      if (event.touches.length === 1 && previewZoom > 1) {
        const [touch] = event.touches;
        previewGestureRef.current.touchMode = 'drag';
        previewGestureRef.current.dragStartX = touch.clientX;
        previewGestureRef.current.dragStartY = touch.clientY;
        previewGestureRef.current.dragStartOffsetX = previewOffset.x;
        previewGestureRef.current.dragStartOffsetY = previewOffset.y;
      }
    }, [previewOffset.x, previewOffset.y, previewZoom]);

    const handlePreviewTouchMove = useCallback((event) => {
      if (event.touches.length === 2 && previewGestureRef.current.touchMode === 'pinch') {
        event.preventDefault();
        const [t1, t2] = event.touches;
        const dx = t2.clientX - t1.clientX;
        const dy = t2.clientY - t1.clientY;
        const distance = Math.hypot(dx, dy);
        const startDistance = previewGestureRef.current.pinchStartDistance || 1;
        const factor = distance / startDistance;
        const nextZoom = clampPreviewZoom(previewGestureRef.current.pinchStartZoom * factor);
        const midX = (t1.clientX + t2.clientX) / 2;
        const midY = (t1.clientY + t2.clientY) / 2;

        const viewport = previewViewportRef.current;
        if (!viewport) return;
        const rect = viewport.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const pointX = midX - rect.left;
        const pointY = midY - rect.top;
        const startZoom = previewGestureRef.current.pinchStartZoom;
        const ratio = nextZoom / startZoom;

        setPreviewZoom(nextZoom);
        setPreviewOffset({
          x: pointX - centerX - (pointX - centerX - previewGestureRef.current.pinchStartOffsetX) * ratio,
          y: pointY - centerY - (pointY - centerY - previewGestureRef.current.pinchStartOffsetY) * ratio,
        });
        return;
      }

      if (event.touches.length === 1 && previewGestureRef.current.touchMode === 'drag' && previewZoom > 1) {
        event.preventDefault();
        const [touch] = event.touches;
        const deltaX = touch.clientX - previewGestureRef.current.dragStartX;
        const deltaY = touch.clientY - previewGestureRef.current.dragStartY;
        setPreviewOffset({
          x: previewGestureRef.current.dragStartOffsetX + deltaX,
          y: previewGestureRef.current.dragStartOffsetY + deltaY,
        });
      }
    }, [previewZoom]);

    const handlePreviewTouchEnd = useCallback(() => {
      previewGestureRef.current.touchMode = null;
    }, []);

    const previewImageAdjuntos = useMemo(
      () => (Array.isArray(adjuntos) ? adjuntos.filter((item) => isImageMimeType(item?.mime_type)) : []),
      [adjuntos]
    );

    const previewAdjuntoIndex = useMemo(() => {
      if (!previewAdjunto?.id) return -1;
      return previewImageAdjuntos.findIndex((item) => item?.id === previewAdjunto.id);
    }, [previewAdjunto, previewImageAdjuntos]);

    const handlePreviewPrev = useCallback(() => {
      if (!previewImageAdjuntos.length || previewAdjuntoIndex < 0) return;
      const targetIndex = (previewAdjuntoIndex - 1 + previewImageAdjuntos.length) % previewImageAdjuntos.length;
      setPreviewAdjunto(previewImageAdjuntos[targetIndex]);
    }, [previewAdjuntoIndex, previewImageAdjuntos]);

    const handlePreviewNext = useCallback(() => {
      if (!previewImageAdjuntos.length || previewAdjuntoIndex < 0) return;
      const targetIndex = (previewAdjuntoIndex + 1) % previewImageAdjuntos.length;
      setPreviewAdjunto(previewImageAdjuntos[targetIndex]);
    }, [previewAdjuntoIndex, previewImageAdjuntos]);

    useEffect(() => {
      if (!previewAdjunto) return;
      const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
          handleClosePreview();
          return;
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          handlePreviewPrev();
          return;
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          handlePreviewNext();
          return;
        }
        if ((event.key === '+' || event.key === '=') && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          handlePreviewZoomIn();
          return;
        }
        if ((event.key === '-' || event.key === '_') && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          handlePreviewZoomOut();
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [previewAdjunto, handleClosePreview, handlePreviewPrev, handlePreviewNext, handlePreviewZoomIn, handlePreviewZoomOut]);

    useEffect(() => {
      if (!previewAdjunto) {
        resetPreviewTransform();
      }
    }, [previewAdjunto, resetPreviewTransform]);

    useEffect(() => {
      const handleWindowMouseMove = (event) => {
        if (!previewGestureRef.current.isDragging) return;
        const deltaX = event.clientX - previewGestureRef.current.dragStartX;
        const deltaY = event.clientY - previewGestureRef.current.dragStartY;
        setPreviewOffset({
          x: previewGestureRef.current.dragStartOffsetX + deltaX,
          y: previewGestureRef.current.dragStartOffsetY + deltaY,
        });
      };

      const handleWindowMouseUp = () => {
        previewGestureRef.current.isDragging = false;
      };

      window.addEventListener('mousemove', handleWindowMouseMove);
      window.addEventListener('mouseup', handleWindowMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleWindowMouseMove);
        window.removeEventListener('mouseup', handleWindowMouseUp);
      };
    }, []);

    useEffect(() => {
      if (!rowQuickView) return;
      const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
          setRowQuickView(null);
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [rowQuickView]);

    useEffect(() => {
      const registroId = rowQuickView?.registroId;
      if (!rowQuickView) {
        setRowQuickViewAdjuntosLoading(false);
        setRowQuickViewAdjuntosCount(null);
        setRowQuickViewAdjuntosError('');
        return;
      }
      if (!registroId) {
        setRowQuickViewAdjuntosLoading(false);
        setRowQuickViewAdjuntosCount(null);
        setRowQuickViewAdjuntosError('No se puede validar adjuntos para este registro.');
        return;
      }

      const controller = new AbortController();
      setRowQuickViewAdjuntosLoading(true);
      setRowQuickViewAdjuntosCount(null);
      setRowQuickViewAdjuntosError('');

      fetch(`${API_BASE_URL}/api/basedatos/${registroId}/adjuntos`, { signal: controller.signal })
        .then(async (response) => {
          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload?.mensaje || 'No se pudieron consultar los adjuntos.');
          }
          return response.json();
        })
        .then((data) => {
          if (controller.signal.aborted) return;
          const list = Array.isArray(data) ? data : [];
          setRowQuickViewAdjuntosCount(list.length);
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          console.error('No se pudo consultar adjuntos desde el modal:', error);
          setRowQuickViewAdjuntosError(error.message || 'No se pudieron consultar los adjuntos.');
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setRowQuickViewAdjuntosLoading(false);
          }
        });

      return () => controller.abort();
    }, [rowQuickView]);

  const handleAutoAssignLocalidades = useCallback(async () => {
    if (isAssigningLocalidades) return;
    setIsAssigningLocalidades(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/basedatos/asignar-localidades`, {
        method: 'POST'
      });
      const payload = await response.json();
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.mensaje || 'No se pudieron asignar las localidades');
      }
      const updated = Number(payload.updated) || 0;
      if (updated > 0) {
        alert(`Se actualizaron ${updated} registros.`);
      } else {
        alert('No se encontraron registros pendientes por asignar.');
      }
      cargarDatos();
    } catch (err) {
      console.error('Error al asignar localidades automáticamente:', err);
      alert('Ocurrió un error al asignar las localidades automáticamente.');
    } finally {
      setIsAssigningLocalidades(false);
    }
  }, [cargarDatos, isAssigningLocalidades]);

  const handleDeleteNuevoEstatus = async () => {
    if (!esSupervisor) return;
    if (!nuevoEstatusGridRef.current) return;
    const selectedRows = nuevoEstatusGridRef.current.api.getSelectedRows();
    if (selectedRows.length === 0) {
      alert('Selecciona al menos un renglón para borrar.');
      return;
    }
    const ids = selectedRows.map(row => row.id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/nuevo-estatus/borrar`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      const data = await res.json();
      if (data.ok) {
        cargarNuevoEstatus();
        setNuevoEstatusSelectedCount(0);
        nuevoEstatusGridRef.current.api.deselectAll();
      } else {
        alert(data.mensaje || 'Error al borrar');
      }
    } catch (err) {
      alert('Error de conexión al borrar');
    }
  };

  const handleNuevoEstatusFileUpload = (e) => {
    if (!puedeGestionarNuevoEstatus) return;
    const file = e.target.files ? e.target.files[0] : null;
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target?.result;
      if (!bstr) return;
      const workbook = XLSX.read(bstr, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      const mappedRows = rawRows.map(raw => {
        const normalized = {};
        Object.entries(raw).forEach(([key, value]) => {
          const normalizedKey = normalizeHeaderKey(key);
          const field = headerLookup.get(normalizedKey) || NUEVO_ESTATUS_EXCEL_HEADER_ALIASES.get(normalizedKey);
          if (field) {
            normalized[field] = value;
          }
        });
        return normalized;
      }).filter(row => Object.keys(row).length > 0);
      setNuevoEstatusExcelData(mappedRows);
      setNuevoEstatusPuedeCargar(mappedRows.length > 0);
      if (nuevoEstatusFileInputRef.current) {
        nuevoEstatusFileInputRef.current.value = '';
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleCargarNuevoEstatusExcel = async () => {
    if (!puedeGestionarNuevoEstatus) return;
    if (!nuevoEstatusExcelData.length) return;
    setNuevoEstatusIsUploading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/nuevo-estatus/insertar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nuevoEstatusExcelData)
      });
      const data = await res.json();
      if (data.ok) {
        setNuevoEstatusExcelData([]);
        setNuevoEstatusPuedeCargar(false);
        if (nuevoEstatusFileInputRef.current) {
          nuevoEstatusFileInputRef.current.value = '';
        }
        if (typeof data.lastUpdated === 'string') {
          setNuevoEstatusLastUpdated(data.lastUpdated);
        }
        cargarNuevoEstatus();
      } else {
        alert(data.mensaje || 'Error al cargar información');
      }
    } catch (err) {
      alert('Error de conexión al cargar información');
    } finally {
      setNuevoEstatusIsUploading(false);
    }
  };

  const applyStoredColumnWidths = useCallback((columnApi) => {
    if (!columnApi) return;
    const storedRaw = localStorage.getItem(COLUMN_WIDTHS_STORAGE_KEY);
    if (!storedRaw) return;
    try {
      const stored = JSON.parse(storedRaw);
      if (!stored || typeof stored !== 'object') return;
      const state = Object.entries(stored)
        .filter(([, width]) => typeof width === 'number' && width > 0)
        .map(([colId, width]) => ({ colId, width }));
      if (!state.length) return;
      columnApi.applyColumnState({ state, applyOrder: false });
    } catch (err) {
      console.warn('No se pudo leer los anchos guardados:', err);
    }
  }, []);

  const handleGridReady = useCallback((params) => {
    applyStoredColumnWidths(params?.columnApi);
  }, [applyStoredColumnWidths]);

  const handleColumnResized = useCallback((event) => {
    if (!event?.finished) return;
    const state = event.api?.getColumnState?.();
    if (!Array.isArray(state)) return;
    const widths = {};
    state.forEach(col => {
      if (!col?.colId) return;
      if (typeof col.width !== 'number' || Number.isNaN(col.width)) return;
      widths[col.colId] = col.width;
    });
    try {
      localStorage.setItem(COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(widths));
    } catch (err) {
      console.warn('No se pudieron guardar los anchos de las columnas:', err);
    }
  }, []);

  useEffect(() => {
    if (!gridRef.current?.columnApi) return;
    applyStoredColumnWidths(gridRef.current.columnApi);
  }, [filteredColumnDefs, applyStoredColumnWidths]);

  useEffect(() => {
    if (!nuevoEstatusGridRef.current?.columnApi) return;
    applyStoredColumnWidths(nuevoEstatusGridRef.current.columnApi);
  }, [filteredColumnDefs, applyStoredColumnWidths]);

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <button
          onClick={() => setActiveTab('principal')}
          style={{
            padding: '8px 16px',
            borderRadius: 12,
            border: activeTab === 'principal' ? '1px solid #9aa5b1' : '1px solid #d0d5dd',
            background: activeTab === 'principal' ? '#eef2ff' : '#f8fafc',
            fontWeight: activeTab === 'principal' ? '600' : '500',
            color: '#1f2937',
            cursor: 'pointer'
          }}
        >
          Base de datos principal
        </button>
        <button
          onClick={() => setActiveTab('nuevo-estatus')}
          style={{
            padding: '8px 16px',
            borderRadius: 12,
            border: activeTab === 'nuevo-estatus' ? '1px solid #9aa5b1' : '1px solid #d0d5dd',
            background: activeTab === 'nuevo-estatus' ? '#eef2ff' : '#f8fafc',
            fontWeight: activeTab === 'nuevo-estatus' ? '600' : '500',
            color: '#1f2937',
            cursor: 'pointer'
          }}
        >
          Nuevo estatus
        </button>
      </div>

      {activeTab === 'principal' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <section style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <h2 style={{ margin: '0 0 4px', fontSize: 24 }}>Base de Datos</h2>
                <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>Filtra, exporta y gestiona la tabla principal.</p>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', textAlign: 'right' }}>
                <span style={{ fontWeight: 700, color: '#047857' }}>Seleccionadas: {selectedCount}</span>
                <span style={{ fontWeight: 700, color: '#3730a3' }}>Total de filas: {principalTotalRows}</span>
                <span style={{ fontWeight: 700, color: '#1d4ed8' }}>Página: {principalPage}/{principalTotalPages}</span>
              </div>
            </div>
            <div
              style={{
                order: 2,
                marginTop: 16,
                marginBottom: 18,
                padding: 16,
                borderRadius: 12,
                border: '1px solid #e5e7eb',
                background: '#f8fafb',
                boxShadow: '0 2px 6px rgba(15, 23, 42, 0.05)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>Filtros y navegacion</div>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                  gap: 12,
                  marginBottom: 14
                }}
              >
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={searchText}
                    onChange={e => {
                      setSearchText(e.target.value);
                      setPrincipalPage(1);
                    }}
                    style={{ flex: '1 1 160px', padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
                  />
                  <select
                    value={searchType}
                    onChange={e => {
                      setSearchType(e.target.value);
                      setPrincipalPage(1);
                    }}
                    style={{ minWidth: 140, padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
                  >
                    <option value="contiene">Que contenga</option>
                    <option value="exacta">Coincidencia exacta</option>
                    <option value="no_contiene">No contiene</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder="Aplicar segundo filtro..."
                    value={secondarySearchText}
                    onChange={e => {
                      setSecondarySearchText(e.target.value);
                      setPrincipalPage(1);
                    }}
                    style={{ flex: '1 1 160px', padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
                  />
                  <select
                    value={secondarySearchType}
                    onChange={e => {
                      setSecondarySearchType(e.target.value);
                      setPrincipalPage(1);
                    }}
                    style={{ minWidth: 140, padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
                  >
                    <option value="contiene">Que contenga</option>
                    <option value="exacta">Coincidencia exacta</option>
                    <option value="no_contiene">No contiene</option>
                  </select>
                  {secondarySearchText && (
                    <button
                      type="button"
                      onClick={() => {
                        setSecondarySearchText('');
                        setSecondarySearchType('contiene');
                        setPrincipalPage(1);
                      }}
                      style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d0d5dd', background: '#f3f4f6', fontWeight: 600 }}
                    >
                      Limpiar
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder="Aplicar tercer filtro..."
                    value={tertiarySearchText}
                    onChange={e => {
                      setTertiarySearchText(e.target.value);
                      setPrincipalPage(1);
                    }}
                    style={{ flex: '1 1 160px', padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
                  />
                  <select
                    value={tertiarySearchType}
                    onChange={e => {
                      setTertiarySearchType(e.target.value);
                      setPrincipalPage(1);
                    }}
                    style={{ minWidth: 140, padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
                  >
                    <option value="contiene">Que contenga</option>
                    <option value="exacta">Coincidencia exacta</option>
                    <option value="no_contiene">No contiene</option>
                  </select>
                  {tertiarySearchText && (
                    <button
                      type="button"
                      onClick={() => {
                        setTertiarySearchText('');
                        setTertiarySearchType('contiene');
                        setPrincipalPage(1);
                      }}
                      style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d0d5dd', background: '#f3f4f6', fontWeight: 600 }}
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => setPrincipalPage(1)}
                  disabled={principalLoading || principalPage <= 1}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 8,
                    border: '1px solid #d0d5dd',
                    background: principalLoading || principalPage <= 1 ? '#f3f4f6' : '#eef2ff',
                    cursor: principalLoading || principalPage <= 1 ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  Primera
                </button>
                <button
                  type="button"
                  onClick={() => setPrincipalPage((prev) => Math.max(1, prev - 1))}
                  disabled={principalLoading || principalPage <= 1}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 8,
                    border: '1px solid #d0d5dd',
                    background: principalLoading || principalPage <= 1 ? '#f3f4f6' : '#eef2ff',
                    cursor: principalLoading || principalPage <= 1 ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  Página anterior
                </button>
                <button
                  type="button"
                  onClick={() => setPrincipalPage((prev) => Math.min(principalTotalPages, prev + 1))}
                  disabled={principalLoading || principalPage >= principalTotalPages}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 8,
                    border: '1px solid #d0d5dd',
                    background: principalLoading || principalPage >= principalTotalPages ? '#f3f4f6' : '#eef2ff',
                    cursor: principalLoading || principalPage >= principalTotalPages ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  Página siguiente
                </button>
                <button
                  type="button"
                  onClick={() => setPrincipalPage(principalTotalPages)}
                  disabled={principalLoading || principalPage >= principalTotalPages}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 8,
                    border: '1px solid #d0d5dd',
                    background: principalLoading || principalPage >= principalTotalPages ? '#f3f4f6' : '#eef2ff',
                    cursor: principalLoading || principalPage >= principalTotalPages ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  Última
                </button>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#334155' }}>
                  Filas por página
                  <select
                    value={principalPageSize}
                    onChange={(e) => {
                      const nextSize = Number(e.target.value) || 300;
                      setPrincipalPageSize(nextSize);
                      setPrincipalPage(1);
                    }}
                    style={{ minWidth: 90, padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
                  >
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={300}>300</option>
                    <option value={500}>500</option>
                  </select>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#334155' }}>
                  Ir a página
                  <select
                    value={goToPageInput}
                    onChange={(e) => {
                      const target = Number(e.target.value);
                      if (!Number.isFinite(target)) return;
                      const safeTarget = Math.min(Math.max(target, 1), principalTotalPages);
                      setGoToPageInput(String(safeTarget));
                      setPrincipalPage(safeTarget);
                    }}
                    disabled={principalLoading}
                    style={{ minWidth: 130, padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
                  >
                    {Array.from({ length: principalTotalPages }, (_, index) => {
                      const pageNumber = index + 1;
                      return (
                        <option key={pageNumber} value={pageNumber}>
                          Página {pageNumber}
                        </option>
                      );
                    })}
                  </select>
                </label>
                {principalLoading && <span style={{ fontWeight: 600, color: '#2563eb' }}>Actualizando datos...</span>}
                <button
                  onClick={handleAutoAssignLocalidades}
                  disabled={isAssigningLocalidades}
                  style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #d1fae5', background: isAssigningLocalidades ? '#e5e7eb' : '#bbf7d0', cursor: isAssigningLocalidades ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                >
                  {isAssigningLocalidades ? 'Asignando...' : 'Completar localidades'}
                </button>
                <button
                  type="button"
                  onClick={handleExportPrincipalToExcel}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 8,
                    border: '1px solid #d0d5dd',
                    background: '#fef3c7',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  Exportar vista
                </button>
                <button
                  type="button"
                  onClick={handleUndo}
                  disabled={!canUndo}
                  title="Deshacer el último cambio (máximo 5)"
                  style={{
                    padding: '6px 14px',
                    borderRadius: 8,
                    border: '1px solid #d0d5dd',
                    background: canUndo ? '#e0e7ff' : '#f3f4f6',
                    cursor: canUndo ? 'pointer' : 'not-allowed',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  <span style={{ fontSize: 16 }}>↶</span>
                  Deshacer
                </button>
                <button
                  onClick={() => setShowColumnListPrincipal(prev => !prev)}
                  style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #d0d5dd', background: showColumnListPrincipal ? '#e0e7ff' : '#f3f4f6', cursor: 'pointer', fontWeight: 600 }}
                >
                  {showColumnListPrincipal ? 'Ocultar selector de columnas' : 'Seleccionar columnas'}
                </button>
              </div>
            </div>
            <section
              style={{
                order: 3,
                background: '#f8fafc',
                border: '1px solid #e5e7eb',
                borderRadius: 16,
                padding: 16,
                marginBottom: 12,
                boxShadow: '0 8px 18px rgba(15, 23, 42, 0.05)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>Inspector y adjuntos</div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ flex: '1 1 420px', minWidth: 320, background: '#ffffff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontWeight: 700, color: '#1f2937' }}>{inspectorField ? inspectorHeader : 'Inspector de celdas'}</div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                        {inspectorField ? 'Selecciona una celda para ver su contenido.' : 'No hay una celda seleccionada.'}
                      </div>
                      {inspectorField && (
                        <div style={{ marginTop: 6, fontSize: 11, color: '#475467', lineHeight: 1.5 }}>
                          <div><span style={{ fontWeight: 700 }}>Taller:</span> {inspectorTallerLabel || '-'}</div>
                          <div><span style={{ fontWeight: 700 }}>Modelo:</span> {inspectorModeloAnioLabel || '-'}</div>
                          <div><span style={{ fontWeight: 700 }}>Pedido:</span> {inspectorPedidoLabel || '-'}</div>
                          <div>
                            <span style={{ fontWeight: 700 }}>Siniestro:</span> {inspectorSiniestroLabel || '-'}
                            <span style={{ marginLeft: 8, fontWeight: 700 }}>Item:</span> {inspectorItemLabel || '-'}
                          </div>
                          <div><span style={{ fontWeight: 700 }}>Origen:</span> {inspectorOrigenLabel || '-'}</div>
                          <div style={{ color: '#667085' }}>{inspectorIsEditable ? 'Editable.' : 'Solo lectura.'}</div>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleDialogCancel}
                      onMouseDown={() => { inspectorSuppressCommitRef.current = true; }}
                      disabled={!inspectorField}
                      style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: inspectorField ? '#f9fafb' : '#f3f4f6', cursor: inspectorField ? 'pointer' : 'not-allowed', fontWeight: 600 }}
                    >
                      Limpiar selección
                    </button>
                  </div>

                  {inspectorField ? (
                    inspectorIsEditable ? (
                      getSelectFieldOptions(inspectorField) ? (
                        <select
                          ref={dialogInputRef}
                          value={inspectorValue}
                          onChange={handleDialogValueChange}
                          onBlur={handleDialogBlur}
                          onKeyDown={handleDialogKeyDown}
                          style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid #d1d5db', background: '#f9fafb', fontSize: 13 }}
                        >
                          {inspectorField === 'LOCALIDAD' && <option value="">Selecciona una opción</option>}
                          {getSelectFieldOptions(inspectorField).map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : (
                        <textarea
                          ref={dialogInputRef}
                          value={inspectorValue}
                          onChange={handleDialogValueChange}
                          onBlur={handleDialogBlur}
                          onKeyDown={handleDialogKeyDown}
                          rows={3}
                          placeholder="Escribe el valor..."
                          style={{ width: '100%', padding: 12, borderRadius: 10, border: '1px solid #d1d5db', resize: 'vertical', fontSize: 13, minHeight: 80 }}
                        />
                      )
                    ) : (
                      <div style={{ minHeight: 80, borderRadius: 10, border: '1px solid #d1d5db', background: '#f9fafb', padding: 12, fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {inspectorValue || '(vacío)'}
                      </div>
                    )
                  ) : (
                    <div style={{ minHeight: 80, borderRadius: 10, border: '1px solid #e5e7eb', background: '#f9fafb', padding: 12, fontSize: 13, color: '#6b7280' }}>
                      No hay una celda seleccionada.
                    </div>
                  )}
                </div>

                <div style={{ flex: '1 1 420px', minWidth: 320, background: '#ffffff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontWeight: 700, color: '#1f2937' }}>
                        Adjuntos ({hasRegistroSeleccionado ? `${adjuntos.length}/${ADJUNTOS_MAX_PER_REGISTRO}` : '—'})
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                        Puedes cargar PDF, imágenes o documentos (máx. 25 MB c/u).
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <input
                        ref={adjuntosFileInputRef}
                        type="file"
                        style={{ display: 'none' }}
                        multiple
                        accept={ADJUNTOS_ACCEPTED_EXTENSIONS}
                        onChange={handleAdjuntosFileChange}
                      />
                      <button
                        type="button"
                        onClick={handleAdjuntosReload}
                        disabled={adjuntosLoading || !hasRegistroSeleccionado}
                        style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d0d5dd', background: adjuntosLoading ? '#f3f4f6' : '#e0f2fe', cursor: adjuntosLoading ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                      >
                        {adjuntosLoading ? 'Actualizando...' : 'Actualizar lista'}
                      </button>
                      <button
                        type="button"
                        onClick={handleAdjuntosUploadClick}
                        disabled={!hasRegistroSeleccionado || adjuntosUploading || adjuntosSlotsDisponibles <= 0}
                        style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #c7d2fe', background: adjuntosUploading || adjuntosSlotsDisponibles <= 0 ? '#f3f4f6' : '#dbeafe', cursor: adjuntosUploading || adjuntosSlotsDisponibles <= 0 ? 'not-allowed' : 'pointer', fontWeight: 600, color: '#1d4ed8' }}
                      >
                        {adjuntosUploading ? 'Subiendo...' : !hasRegistroSeleccionado ? 'Selecciona un registro' : adjuntosSlotsDisponibles > 0 ? 'Agregar archivos' : 'Máximo alcanzado'}
                      </button>
                    </div>
                  </div>

                  <div style={{ border: '1px solid #d4d7dd', borderRadius: 10, padding: 10, background: '#f9fafb', minHeight: 120, maxHeight: 210, overflowY: 'auto' }}>
                    {!hasRegistroSeleccionado ? (
                      <div style={{ fontSize: 13, color: '#6b7280' }}>Selecciona un registro para consultar o subir sus archivos adjuntos.</div>
                    ) : adjuntosError ? (
                      <div style={{ fontSize: 13, color: '#b42318' }}>{adjuntosError}</div>
                    ) : adjuntosLoading ? (
                      <div style={{ fontSize: 13, color: '#475467' }}>Cargando adjuntos...</div>
                    ) : adjuntos.length === 0 ? (
                      <div style={{ fontSize: 13, color: '#6b7280' }}>No hay archivos adjuntos para este registro.</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {adjuntos.map((archivo) => {
                          const esImagen = isImageMimeType(archivo.mime_type);
                          const esPdf = isPdfAdjunto(archivo);
                          const extension = getAdjuntoExtension(archivo) || 'FILE';
                          const esPrevisualizable = esImagen || esPdf;
                          return (
                            <div key={archivo.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', border: '1px solid #e5e7eb', borderRadius: 10, padding: 8, background: '#ffffff' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: '1 1 280px' }}>
                                {esImagen && (
                                  <button
                                    type="button"
                                    onClick={() => handleAdjuntoPreview(archivo)}
                                    title="Ver imagen"
                                    style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                  >
                                    <img
                                      src={buildAdjuntoViewUrl(archivo)}
                                      alt={archivo.original_name || archivo.file_name || 'Miniatura'}
                                      loading="lazy"
                                      style={{ width: 54, height: 54, objectFit: 'cover', borderRadius: 8, border: '1px solid #dbe3ef', background: '#f8fafc' }}
                                    />
                                  </button>
                                )}
                                {!esImagen && esPdf && (
                                  <button
                                    type="button"
                                    onClick={() => handleAdjuntoPreview(archivo)}
                                    title="Ver PDF"
                                    style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                  >
                                    <iframe
                                      src={`${buildAdjuntoViewUrl(archivo)}#toolbar=0&navpanes=0&scrollbar=0`}
                                      title={archivo.original_name || archivo.file_name || 'Vista previa PDF'}
                                      style={{ width: 54, height: 54, border: '1px solid #dbe3ef', borderRadius: 8, background: '#f8fafc', pointerEvents: 'none' }}
                                    />
                                  </button>
                                )}
                                {!esPrevisualizable && (
                                  <div
                                    title={extension}
                                    style={{ width: 54, height: 54, border: '1px solid #dbe3ef', borderRadius: 8, background: '#f8fafc', color: '#1e3a8a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 11, letterSpacing: 0.4, flex: '0 0 auto' }}
                                  >
                                    {extension}
                                  </div>
                                )}
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: 600, color: '#0f172a', wordBreak: 'break-word' }}>{archivo.file_name}</div>
                                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                                    {formatFileSizeLabel(archivo.file_size)}
                                    {archivo.created_at ? ` · ${formatAdjuntoFecha(archivo.created_at)}` : ''}
                                  </div>
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  onClick={() => handleAdjuntoPreview(archivo)}
                                  style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#3730a3', fontWeight: 600, cursor: 'pointer' }}
                                >
                                  {esPrevisualizable ? 'Ver' : 'Abrir'}
                                </button>
                                <a
                                  href={`${API_BASE_URL}/api/basedatos/adjuntos/${archivo.id}/download`}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontWeight: 600, textDecoration: 'none' }}
                                >
                                  Descargar
                                </a>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {showColumnListPrincipal && (
              <div style={{ order: 4, marginBottom: 12, padding: 12, borderRadius: 12, border: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', flexWrap: 'wrap', gap: 10, maxHeight: 220, overflowY: 'auto' }}>
                {baseColumnFields.map(field => (
                  <label key={field} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 200, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={columnVisibility[field] !== false}
                      onChange={() => handleToggleColumnVisibility(field)}
                    />
                    <span>{columnLabels[field] || field}</span>
                  </label>
                ))}
              </div>
            )}
            <div style={{ order: 5, border: '1px solid #e2e8f0', borderRadius: 14, background: '#f8fafc', padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>Tabla principal</div>
              </div>
              <div className="ag-theme-alpine"
                style={{
                  height: 500,
                  width: '100%',
                  fontSize: '13px',
                  overflow: 'auto',
                  borderRadius: 12
                }}>
                <AgGridReact
                  ref={gridRef}
                  columnDefs={filteredColumnDefs}
                  rowData={filteredDataMemo}
                  rowSelection="multiple"
                  getRowId={getPrincipalRowId}
                  getRowClass={getPrincipalRowClass}
                  domLayout="normal"
                  suppressHorizontalScroll={false}
                  suppressMovableColumns={true}
                  enableBrowserTooltips={true}
                  enableCellTextSelection={true}
                  deltaRowDataMode={true}
                  defaultColDef={{
                    resizable: true,
                    sortable: false,
                    filter: false,
                    minWidth: 40,
                    editable: false,
                  }}
                  headerHeight={32}
                  rowHeight={28}
                  onSelectionChanged={onSelectionChanged}
                  onCellClicked={handleCellClicked}
                  onCellFocused={handleCellFocused}
                  onCellValueChanged={handlePrincipalCellEdit}
                  onGridReady={handleGridReady}
                  onColumnResized={handleColumnResized}
                  singleClickEdit={true}
                  stopEditingWhenCellsLoseFocus={true}
                />
              </div>
            </div>
          </section>
        </div>
      )}

      {activeTab === 'nuevo-estatus' && (
        <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 20 }}>
          <h2 style={{ marginTop: 0 }}>Nuevo estatus</h2>
          <p style={{ marginTop: 0, color: '#6b7280' }}>
            Tabla auxiliar con las mismas columnas de la base de datos para un nuevo seguimiento de estatus.
          </p>
          <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            {puedeGestionarNuevoEstatus && (
              <>
                <input
                  ref={nuevoEstatusFileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleNuevoEstatusFileUpload}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => nuevoEstatusFileInputRef.current?.click()}
                  disabled={nuevoEstatusIsUploading}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 8,
                    border: '1px solid #d0d5dd',
                    background: nuevoEstatusIsUploading ? '#e5e7eb' : '#e0f2fe',
                    cursor: nuevoEstatusIsUploading ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  Seleccionar Excel
                </button>
                <button
                  type="button"
                  onClick={handleCargarNuevoEstatusExcel}
                  disabled={!nuevoEstatusPuedeCargar || nuevoEstatusIsUploading}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 8,
                    border: '1px solid #d0d5dd',
                    background: !nuevoEstatusPuedeCargar || nuevoEstatusIsUploading ? '#e5e7eb' : '#dcfce7',
                    cursor: !nuevoEstatusPuedeCargar || nuevoEstatusIsUploading ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  {nuevoEstatusIsUploading ? 'Cargando...' : 'Cargar Excel'}
                </button>
                {nuevoEstatusExcelData.length > 0 && (
                  <span style={{ fontWeight: 600, color: '#1d4ed8' }}>
                    Registros listos: {nuevoEstatusExcelData.length}
                  </span>
                )}
              </>
            )}
            {esSupervisor && (
              <button
                onClick={handleDeleteNuevoEstatus}
                style={{ background: '#dc2626', color: '#fff', fontWeight: 'bold', padding: '6px 14px', borderRadius: 8, border: 'none' }}
                disabled={nuevoEstatusSelectedCount === 0}
              >
                Borrar seleccionados
              </button>
            )}
            <span style={{ fontWeight: 'bold', color: '#047857' }}>Seleccionadas: {nuevoEstatusSelectedCount}</span>
            <span style={{ fontWeight: 'bold', color: '#3730a3' }}>Total de filas: {filteredNuevoEstatus.length}</span>
            {nuevoEstatusLastUpdatedLabel && (
              <span style={{ fontWeight: 600, color: '#475467' }}>
                Ultima actualizacion: {nuevoEstatusLastUpdatedLabel}
              </span>
            )}
            <button
              type="button"
              onClick={() => setShowColumnListNuevo((prev) => !prev)}
              style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #d0d5dd', background: showColumnListNuevo ? '#e0e7ff' : '#f3f4f6', cursor: 'pointer', fontWeight: 600 }}
            >
              {showColumnListNuevo ? 'Ocultar columnas' : 'Seleccionar columnas'}
            </button>
            <input
              type="text"
              placeholder="Buscar en nuevo estatus..."
              value={searchNuevoText}
              onChange={e => setSearchNuevoText(e.target.value)}
              style={{ padding: 6, minWidth: 260, borderRadius: 8, border: '1px solid #d0d5dd' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#334155' }}>
              Campo de fecha
              <select
                value={nuevoFechaFilterField}
                onChange={(e) => setNuevoFechaFilterField(e.target.value)}
                style={{ padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
              >
                {NUEVO_ESTATUS_DATE_FIELD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#334155' }}>
              Desde
              <input
                type="date"
                value={nuevoCancelacionStartDate}
                onChange={(e) => setNuevoCancelacionStartDate(e.target.value)}
                style={{ padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#334155' }}>
              Hasta
              <input
                type="date"
                value={nuevoCancelacionEndDate}
                onChange={(e) => setNuevoCancelacionEndDate(e.target.value)}
                style={{ padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
              />
            </label>
            {(nuevoCancelacionStartDate || nuevoCancelacionEndDate) && (
              <button
                type="button"
                onClick={() => {
                  setNuevoCancelacionStartDate('');
                  setNuevoCancelacionEndDate('');
                }}
                style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #d0d5dd', background: '#fef3c7', cursor: 'pointer', fontWeight: 600 }}
              >
                Limpiar rango
              </button>
            )}
            <button
              type="button"
              onClick={handleExportNuevoEstatusSelection}
              disabled={nuevoEstatusSelectedCount === 0}
              style={{
                padding: '6px 14px',
                borderRadius: 8,
                border: '1px solid #d0d5dd',
                background: nuevoEstatusSelectedCount > 0 ? '#dbeafe' : '#e5e7eb',
                cursor: nuevoEstatusSelectedCount > 0 ? 'pointer' : 'not-allowed',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 64 64"
                aria-hidden="true"
                focusable="false"
                style={{ display: 'block' }}
              >
                <path d="M14 6h28l12 12v34a6 6 0 0 1-6 6H14a6 6 0 0 1-6-6V12a6 6 0 0 1 6-6z" fill="#0f9d58" />
                <path d="M42 6v12h12" fill="#0c7a43" />
                <path d="M30 54V26h24v28z" fill="#e8f0fe" />
                <path d="M34 32h8v4h-8zm0 8h8v4h-8zm0 8h8v4h-8zm12-16h4v4h-4zm0 8h4v4h-4zm0 8h4v4h-4z" fill="#c1cedb" />
                <path d="M12 46l10-14-9-14h8l5 8 5-8h8l-9 14 10 14h-8l-6-9-6 9z" fill="#ffffff" />
              </svg>
              <span>Exportar selección</span>
            </button>
          </div>
          <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => nuevoEstatusGridRef.current?.api?.paginationGoToFirstPage()}
              disabled={nuevoEstatusPage <= 1}
              style={{
                padding: '6px 14px',
                borderRadius: 8,
                border: '1px solid #d0d5dd',
                background: nuevoEstatusPage <= 1 ? '#f3f4f6' : '#eef2ff',
                cursor: nuevoEstatusPage <= 1 ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              Primera
            </button>
            <button
              type="button"
              onClick={() => nuevoEstatusGridRef.current?.api?.paginationGoToPreviousPage()}
              disabled={nuevoEstatusPage <= 1}
              style={{
                padding: '6px 14px',
                borderRadius: 8,
                border: '1px solid #d0d5dd',
                background: nuevoEstatusPage <= 1 ? '#f3f4f6' : '#eef2ff',
                cursor: nuevoEstatusPage <= 1 ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              Página anterior
            </button>
            <button
              type="button"
              onClick={() => nuevoEstatusGridRef.current?.api?.paginationGoToNextPage()}
              disabled={nuevoEstatusPage >= nuevoEstatusTotalPages}
              style={{
                padding: '6px 14px',
                borderRadius: 8,
                border: '1px solid #d0d5dd',
                background: nuevoEstatusPage >= nuevoEstatusTotalPages ? '#f3f4f6' : '#eef2ff',
                cursor: nuevoEstatusPage >= nuevoEstatusTotalPages ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              Página siguiente
            </button>
            <button
              type="button"
              onClick={() => nuevoEstatusGridRef.current?.api?.paginationGoToPage(Math.max(nuevoEstatusTotalPages - 1, 0))}
              disabled={nuevoEstatusPage >= nuevoEstatusTotalPages}
              style={{
                padding: '6px 14px',
                borderRadius: 8,
                border: '1px solid #d0d5dd',
                background: nuevoEstatusPage >= nuevoEstatusTotalPages ? '#f3f4f6' : '#eef2ff',
                cursor: nuevoEstatusPage >= nuevoEstatusTotalPages ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              Última
            </button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#334155' }}>
              Filas por página
              <select
                value={nuevoEstatusPageSize}
                onChange={(e) => setNuevoEstatusPageSize(Number(e.target.value))}
                style={{ minWidth: 90, padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
              >
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={300}>300</option>
                <option value={500}>500</option>
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#334155' }}>
              Ir a página
              <select
                value={nuevoEstatusGoToPageInput}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setNuevoEstatusGoToPageInput(nextValue);
                  const page = Number(nextValue);
                  if (Number.isInteger(page) && page >= 1) {
                    nuevoEstatusGridRef.current?.api?.paginationGoToPage(page - 1);
                  }
                }}
                style={{ minWidth: 130, padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
              >
                {Array.from({ length: nuevoEstatusTotalPages }, (_, index) => {
                  const page = index + 1;
                  return (
                    <option key={`nuevo-page-${page}`} value={String(page)}>
                      Página {page}
                    </option>
                  );
                })}
              </select>
            </label>
            <span style={{ fontWeight: 700, color: '#1d4ed8' }}>
              Página: {nuevoEstatusPage}/{nuevoEstatusTotalPages}
            </span>
          </div>
          {showColumnListNuevo && (
            <div style={{ marginBottom: 12, padding: 12, borderRadius: 12, border: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', flexWrap: 'wrap', gap: 10, maxHeight: 220, overflowY: 'auto' }}>
              {baseColumnFields.map(field => (
                <label key={`nuevo-${field}`} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 200, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={columnVisibility[field] !== false}
                    onChange={() => handleToggleColumnVisibility(field)}
                  />
                  <span>{columnLabels[field] || field}</span>
                </label>
              ))}
            </div>
          )}
          <div className="ag-theme-alpine"
            style={{
              height: 500,
              width: '100%',
              fontSize: '13px',
              overflow: 'auto',
              borderRadius: 12
            }}>
            <AgGridReact
              ref={nuevoEstatusGridRef}
              columnDefs={filteredColumnDefs}
              rowData={filteredNuevoEstatus}
              rowSelection="multiple"
              pagination={true}
              paginationPageSize={nuevoEstatusPageSize}
              suppressPaginationPanel={true}
              getRowId={getNuevoEstatusRowId}
              domLayout="normal"
              suppressHorizontalScroll={false}
              suppressMovableColumns={true}
              enableBrowserTooltips={true}
              enableCellTextSelection={true}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: false,
                minWidth: 40,
                editable: false,
              }}
              headerHeight={32}
              rowHeight={28}
              onSelectionChanged={onNuevoEstatusSelectionChanged}
              onCellClicked={handleNuevoEstatusCellClicked}
              onGridReady={handleGridReady}
              onColumnResized={handleColumnResized}
              onPaginationChanged={updateNuevoEstatusPaginationState}
            />
          </div>
        </div>
      )}

      {rowQuickView && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={handleCloseRowQuickView}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9998,
            padding: 16
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: 'min(640px, 94vw)',
              background: '#ffffff',
              borderRadius: 14,
              border: '1px solid #e5e7eb',
              boxShadow: '0 24px 48px rgba(2, 6, 23, 0.25)',
              overflow: 'hidden',
              maxHeight: '86vh',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{rowQuickView.title}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{rowQuickView.subtitle}</div>
              </div>
              <button
                type="button"
                onClick={handleCloseRowQuickView}
                style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid #d1d5db', background: '#ffffff', color: '#111827', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}
              >
                Cerrar
              </button>
            </div>

            <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto' }}>
              {rowQuickViewEditError && (
                <div style={{ border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px', background: '#fff1f2', color: '#991b1b', fontSize: 12, fontWeight: 600 }}>
                  {rowQuickViewEditError}
                </div>
              )}
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <div style={{ fontSize: 12, color: '#475467', fontWeight: 600 }}>Adjuntos</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', textAlign: 'right' }}>
                  {rowQuickViewAdjuntosLoading
                    ? 'Consultando adjuntos...'
                    : rowQuickViewAdjuntosError
                      ? rowQuickViewAdjuntosError
                      : rowQuickViewAdjuntosCount > 0
                        ? `Tiene ${rowQuickViewAdjuntosCount} archivo(s) adjunto(s).`
                        : 'No tiene archivos adjuntos.'}
                </div>
              </div>
              {rowQuickView.fields.map((field) => (
                <div key={field.field} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', background: '#fcfcfd', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                  <div style={{ minWidth: 150, fontSize: 12, color: '#64748b', fontWeight: 600 }}>{field.label}</div>
                  {field.editable ? (
                    <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {getSelectFieldOptions(field.field) ? (
                        <select
                          value={field.value}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            handleRowQuickViewFieldChange(field.field, nextValue);
                            handleRowQuickViewFieldCommit(field.field);
                          }}
                          disabled={rowQuickViewSavingField === field.field}
                          style={{ width: '100%', padding: '6px 8px', borderRadius: 7, border: '1px solid #cbd5e1', background: '#ffffff', fontSize: 12 }}
                        >
                          {field.field === 'LOCALIDAD' && <option value="">Selecciona una opcion</option>}
                          {getSelectFieldOptions(field.field).map((option) => (
                            <option key={option || 'vacio'} value={option}>{option || '(vacio)'}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={field.value}
                          onChange={(event) => handleRowQuickViewFieldChange(field.field, event.target.value)}
                          onBlur={() => handleRowQuickViewFieldCommit(field.field)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              event.currentTarget.blur();
                            }
                          }}
                          disabled={rowQuickViewSavingField === field.field}
                          style={{ width: '100%', padding: '6px 8px', borderRadius: 7, border: '1px solid #cbd5e1', background: '#ffffff', fontSize: 12 }}
                        />
                      )}
                      <div style={{ fontSize: 10, color: '#2563eb', fontWeight: 600 }}>
                        {rowQuickViewSavingField === field.field ? 'Guardando...' : 'Editable con permisos'}
                      </div>
                    </div>
                  ) : (
                    <div style={{ flex: '1 1 260px', fontSize: 12, color: '#0f172a', fontWeight: 700, wordBreak: 'break-word' }}>
                      {field.value == null || String(field.value).trim() === '' ? '(vacio)' : String(field.value)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {previewAdjunto && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={handleClosePreview}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 8,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#0f172a',
              padding: 16,
              borderRadius: 12,
              width: '96vw',
              maxWidth: '96vw',
              maxHeight: '96vh',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {(() => {
              const previewEsImagen = isImageMimeType(previewAdjunto?.mime_type);
              const previewEsPdf = isPdfAdjunto(previewAdjunto);
              return (
            <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20 }}>
              <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {previewAdjunto.original_name || previewAdjunto.file_name || 'Archivo adjunto'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {previewEsImagen && previewImageAdjuntos.length > 1 && (
                  <span style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600 }}>
                    {previewAdjuntoIndex >= 0 ? `${previewAdjuntoIndex + 1}/${previewImageAdjuntos.length}` : `0/${previewImageAdjuntos.length}`}
                  </span>
                )}
                {(previewEsImagen || previewEsPdf) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    onClick={handlePreviewZoomOut}
                    style={{
                      background: '#1e293b',
                      border: '1px solid #334155',
                      color: '#e2e8f0',
                      fontWeight: 700,
                      padding: '6px 10px',
                      borderRadius: 999,
                      cursor: 'pointer',
                    }}
                  >
                    -
                  </button>
                  <span style={{ color: '#e2e8f0', minWidth: 64, textAlign: 'center', fontWeight: 700, fontSize: 12 }}>
                    {Math.round(previewZoom * 100)}%
                  </span>
                  <button
                    onClick={handlePreviewZoomIn}
                    style={{
                      background: '#1e293b',
                      border: '1px solid #334155',
                      color: '#e2e8f0',
                      fontWeight: 700,
                      padding: '6px 10px',
                      borderRadius: 999,
                      cursor: 'pointer',
                    }}
                  >
                    +
                  </button>
                  <button
                    onClick={resetPreviewTransform}
                    style={{
                      background: '#1e293b',
                      border: '1px solid #334155',
                      color: '#e2e8f0',
                      fontWeight: 700,
                      padding: '6px 10px',
                      borderRadius: 999,
                      cursor: 'pointer',
                    }}
                  >
                    Ajustar
                  </button>
                </div>
                )}
                {previewEsImagen && previewImageAdjuntos.length > 1 && (
                  <button
                    onClick={handlePreviewPrev}
                    style={{
                      background: '#1e293b',
                      border: '1px solid #334155',
                      color: '#e2e8f0',
                      fontWeight: 700,
                      padding: '6px 10px',
                      borderRadius: 999,
                      cursor: 'pointer',
                    }}
                  >
                    ◀
                  </button>
                )}
                {previewEsImagen && previewImageAdjuntos.length > 1 && (
                  <button
                    onClick={handlePreviewNext}
                    style={{
                      background: '#1e293b',
                      border: '1px solid #334155',
                      color: '#e2e8f0',
                      fontWeight: 700,
                      padding: '6px 10px',
                      borderRadius: 999,
                      cursor: 'pointer',
                    }}
                  >
                    ▶
                  </button>
                )}
                {previewEsPdf && previewPdfPages > 1 && (
                  <button
                    onClick={() => setPreviewPdfCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={previewPdfCurrentPage <= 1}
                    style={{
                      background: '#1e293b',
                      border: '1px solid #334155',
                      color: '#e2e8f0',
                      fontWeight: 700,
                      padding: '6px 10px',
                      borderRadius: 999,
                      cursor: previewPdfCurrentPage <= 1 ? 'not-allowed' : 'pointer',
                      opacity: previewPdfCurrentPage <= 1 ? 0.6 : 1
                    }}
                  >
                    Pagina -
                  </button>
                )}
                {previewEsPdf && previewPdfPages > 1 && (
                  <span style={{ color: '#94a3b8', fontSize: 12, fontWeight: 700 }}>
                    {previewPdfCurrentPage}/{previewPdfPages}
                  </span>
                )}
                {previewEsPdf && previewPdfPages > 1 && (
                  <button
                    onClick={() => setPreviewPdfCurrentPage((prev) => Math.min(previewPdfPages, prev + 1))}
                    disabled={previewPdfCurrentPage >= previewPdfPages}
                    style={{
                      background: '#1e293b',
                      border: '1px solid #334155',
                      color: '#e2e8f0',
                      fontWeight: 700,
                      padding: '6px 10px',
                      borderRadius: 999,
                      cursor: previewPdfCurrentPage >= previewPdfPages ? 'not-allowed' : 'pointer',
                      opacity: previewPdfCurrentPage >= previewPdfPages ? 0.6 : 1
                    }}
                  >
                    Pagina +
                  </button>
                )}
                <button
                  onClick={handleClosePreview}
                  style={{
                    background: '#f87171',
                    border: 'none',
                    color: '#fff',
                    fontWeight: 600,
                    padding: '6px 14px',
                    borderRadius: 999,
                    cursor: 'pointer',
                  }}
                >
                  Cerrar
                </button>
              </div>
            </div>
            <div
              ref={previewViewportRef}
              onWheel={previewEsImagen || previewEsPdf ? handlePreviewWheel : undefined}
              onMouseDown={previewEsImagen || previewEsPdf ? handlePreviewMouseDown : undefined}
              onTouchStart={previewEsImagen || previewEsPdf ? handlePreviewTouchStart : undefined}
              onTouchMove={previewEsImagen || previewEsPdf ? handlePreviewTouchMove : undefined}
              onTouchEnd={previewEsImagen || previewEsPdf ? handlePreviewTouchEnd : undefined}
              style={{
                background: '#020617',
                borderRadius: 8,
                padding: 12,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: 320,
                minHeight: 320,
                height: 'calc(96vh - 110px)',
                gap: 10,
                overflow: 'hidden',
                touchAction: previewEsImagen || previewEsPdf ? 'none' : 'auto',
              }}
            >
              {previewEsImagen && previewImageAdjuntos.length > 1 && (
                <button
                  onClick={handlePreviewPrev}
                  style={{
                    background: 'rgba(15, 23, 42, 0.7)',
                    border: '1px solid #334155',
                    color: '#e2e8f0',
                    fontWeight: 700,
                    fontSize: 18,
                    width: 40,
                    height: 40,
                    borderRadius: 999,
                    cursor: 'pointer',
                    flex: '0 0 auto'
                  }}
                >
                  ◀
                </button>
              )}
              {previewEsImagen && (
                <img
                  src={buildAdjuntoViewUrl(previewAdjunto)}
                  alt={previewAdjunto.original_name || previewAdjunto.file_name || 'Vista previa'}
                  draggable={false}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: 6,
                    boxShadow: '0 0 30px rgba(15,23,42,0.6)',
                    transform: `translate(${previewOffset.x}px, ${previewOffset.y}px) scale(${previewZoom})`,
                    transformOrigin: 'center center',
                    transition: previewGestureRef.current.isDragging || previewGestureRef.current.touchMode ? 'none' : 'transform 0.08s ease-out',
                    cursor: previewZoom > 1 ? 'grab' : 'default',
                    userSelect: 'none',
                  }}
                />
              )}
              {previewEsImagen && previewImageAdjuntos.length > 1 && (
                <button
                  onClick={handlePreviewNext}
                  style={{
                    background: 'rgba(15, 23, 42, 0.7)',
                    border: '1px solid #334155',
                    color: '#e2e8f0',
                    fontWeight: 700,
                    fontSize: 18,
                    width: 40,
                    height: 40,
                    borderRadius: 999,
                    cursor: 'pointer',
                    flex: '0 0 auto'
                  }}
                >
                  ▶
                </button>
              )}
              {!previewEsImagen && previewEsPdf && (
                <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 8, overflow: 'hidden' }}>
                                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <a
                                      href={buildAdjuntoViewUrl(previewAdjunto)}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontWeight: 600, textDecoration: 'none', fontSize: 12 }}
                                    >
                                      Abrir PDF en pestaña nueva
                                    </a>
                                  </div>
                                  <div style={{ width: '100%', minHeight: 320, border: '1px solid #1e293b', borderRadius: 8, background: '#ffffff', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 8, overflow: 'hidden' }}>
                                    <div
                                      style={{
                                        transform: `translate(${previewOffset.x}px, ${previewOffset.y}px) scale(${previewZoom})`,
                                        transformOrigin: 'center center',
                                        transition: previewGestureRef.current.isDragging || previewGestureRef.current.touchMode ? 'none' : 'transform 0.08s ease-out',
                                        cursor: previewZoom > 1 ? 'grab' : 'default',
                                        userSelect: 'none'
                                      }}
                                    >
                                    <Document
                                      file={buildAdjuntoViewUrl(previewAdjunto)}
                                      onLoadSuccess={({ numPages }) => {
                                        const total = Number(numPages) || 0;
                                        setPreviewPdfPages(total);
                                        setPreviewPdfCurrentPage((prev) => {
                                          if (!total) return 1;
                                          if (prev < 1) return 1;
                                          if (prev > total) return total;
                                          return prev;
                                        });
                                        setPreviewPdfLoadError('');
                                      }}
                                      onLoadError={() => {
                                        setPreviewPdfLoadError('No se pudo renderizar el PDF en el visor interno.');
                                      }}
                                      loading={<div style={{ padding: 16, color: '#334155', fontWeight: 600 }}>Cargando PDF...</div>}
                                      error={<div style={{ padding: 16, color: '#991b1b', fontWeight: 700 }}>No se pudo cargar el PDF.</div>}
                                    >
                                      <Page
                                        pageNumber={previewPdfCurrentPage}
                                        scale={1.25}
                                        renderTextLayer={false}
                                        renderAnnotationLayer={false}
                                      />
                                    </Document>
                                    </div>
                                  </div>
                                  {previewPdfLoadError && (
                                    <div style={{ color: '#991b1b', fontSize: 12, fontWeight: 700 }}>
                                      {previewPdfLoadError}
                                    </div>
                                  )}
                                </div>
              )}
              {!previewEsImagen && !previewEsPdf && (
                <div style={{ color: '#e2e8f0', fontSize: 13 }}>
                  Este tipo de archivo no tiene vista previa integrada. Usa "Abrir" o "Descargar".
                </div>
              )}
            </div>
            </>
              );
            })()}
          </div>
        </div>
      )}

    </div>
  );
};

export default BaseDatosPage;